import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import { Col, Row, Card, Modal, Button, Badge } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import swal from "sweetalert";
import Alert from "sweetalert2";
import { db, storage } from "../../../firebase";
import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  addDoc,
  orderBy,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";

import Toast, {
  notifyTopRight,
  notifyError,
  notifyWarning,
} from "../../components/toast/SetToast";
import { AuthContext } from "../../../context/AuthContext";
import * as XLSX from "xlsx";
import {
  buildCpapFollowupFileData,
  getCpapFollowupFileFolder,
  normalizeSleepStudyReportDocs,
} from "./cpapFollowupFileUtils";
import {
  FOLLOWUP_TYPE_LIST,
  getFollowupTypeOptionsForNode,
  getNextFollowupTypeForContinueTherapy as getNextFollowupTypeForNode,
  isFollowupTypeAllowedForNode,
} from "./cpapFollowupTypeUtils";
import {
  getCpapFollowupRole,
  isStaffCpapFollowupViewer,
} from "./cpapFollowupAccess";
import {
  getCpapSideEffectOptionsForDisplay,
  getSavedNonPdfCpapSideEffectValues,
  matchCpapSideEffectPdfTemplate,
  matchesCpapSideEffectOptionText,
  updateCpapSideEffectSelection,
} from "./cpapFollowupSymptomUtils";
import AnnualMaskPurchaseEventSection from "./AnnualMaskPurchaseEventSection";

import { filterCpapFollowupRecords, filterCpapFollowupCustomers, getCpapFollowupAccessKey, isCpapFollowupNodeCustomerScopedUser } from "./cpapFollowupAccess";
import { loadCpapFollowupScope } from "./cpapFollowupDataAccess";

const COLLECTION_NAME = "cpap-followup";
const NO_SLEEP_STUDY_ORDER = "No SS Order";

const CPAP_REPORT_TYPE_OPTIONS = [
  "Apex",
  "Ventmed",
  "Topson",
  "Phillips",
  "Resmed - AirMini",
  "Resmed - Airsense 11",
  "Resmed - Airstart 10",
  "Resmed - Airsense 10",
];

const getDefaultLeakRateForCpapReportType = (reportType = "") =>
  String(reportType || "")
    .toLowerCase()
    .includes("vent")
    ? "35"
    : "24";

const PDFJS_VERSION = "2.16.105";
const PDFJS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
const TESSERACT_SCRIPT_URL =
  "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";

const STATUS_OPTIONS = [
  {
    value: "CPAP Follow Up",
    label: "CPAP Follow Up",
    helper: "Technician needs to follow up with patient",
    color: "#A2D2FF",
    badge: "primary",
  },
  {
    value: "Technical Solution",
    label: "Technical Solution",
    helper: "Technician is helping solve CPAP usage problems",
    color: "#FFD8A8",
    badge: "warning",
  },
  {
    value: "Effective Use",
    label: "Effective Use",
    helper: "Patient is using CPAP well",
    color: "#CAFFBF",
    badge: "success",
  },
  {
    value: "CPAP-Consult",
    label: "CPAP-Consult",
    helper: "Patient needs doctor consult for next treatment step",
    color: "#FFADAD",
    badge: "danger",
  },
  {
    value: "Discontinued",
    label: "Discontinued",
    helper: "Patient stopped using CPAP",
    color: "#D1D5DB",
    badge: "secondary",
  },
];

const DOCTOR_ACTION_OPTIONS = [
  { value: "No Doctor Action", label: "รอการประเมินจากแพทย์" },
  { value: "Adjust Pressure", label: "ปรับ Pressure" },
  { value: "Doctor Appointment", label: "นัดพบแพทย์" },
  { value: "Doctor Phone Consult", label: "แพทย์โทรติดตามอาการ" },
  { value: "Continue Therapy", label: "ใช้เครื่องต่อ" },
];

const CPAP_TYPE_OPTIONS = ["CPAP", "Auto-CPAP", "BiPAP"];

const MASK_TYPE_OPTIONS = [
  "กรุณาเลือก",
  "Nasal Mask AirFit N20",
  "Nasal Mask AirFit N30",
  "Nasal Mask AirFit N30i",
  "Nasal Pillow AirFit P10",
  "Nasal Pillow AirFit P30i",
  "Full Face Mask AirFit F20",
  "Full Face Mask AirTouch F20",
  "Nasal Mask ResComf",
  "Full Face Mask ResComf",
  "Nasal Mask Wizard 310",
  "Full Face Mask Wizard 320",
  "Topson Full Face FM50 Mask",
  "Topson Nasal NM40 Mask",
  "Topson Nasal NM50 Mask",
  "Philips Wisp Nasal Mask",
  "Philips Pico Nasal Mask",
  "Philips Amara Gel Full Face Mask",
  "Nasal Mask",
  "Nasal Pillow",
  "Full Face Mask",
  "Oronasal Mask",
  "Other",
];

const MASK_SIZE_OPTIONS = ["กรุณาเลือก", "XS", "S", "M", "L", "XL", "Other"];

const LEAK_ASSESSMENT_OPTIONS = [
  "กรุณาเลือก",
  "อยู่ในเกณฑ์ดี",
  "สูง ควรได้รับการแก้ไข",
];

const WAKE_FEELING_OPTIONS = [
  "สดชื่นขึ้นมาก",
  "สดชื่นขึ้น",
  "เหมือนเดิม",
  "แย่ลง",
];

const DAYTIME_SLEEPINESS_OPTIONS = [
  "ดีขึ้นมาก",
  "ดีขึ้น",
  "เหมือนเดิม",
  "แย่ลง",
];

const SYMPTOM_IMPORT_DEFAULTS = {
  underlyingDisease: "",
  currentWeight: "",
  weightChange: "",
  essScore: "",
  wakeFeeling: "",
  daytimeSleepiness: "",
  sideEffects: [],
  sideEffectsOther: "",
};

const WEIGHT_COMPARISON_OPTIONS = [
  { label: "เท่าเดิม", aliases: ["เท่าเดิม", "เหมือนเดิม"] },
  {
    label: "เพิ่มขึ้น",
    aliases: ["เพิ่มขึ้น", "เพิ่มขึน", "เพิมขึ้น", "เพิมขึน"],
  },
  { label: "ลดลง", aliases: ["ลดลง"] },
];

const formatLocalDate = (dateValue = new Date()) => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const emptyForm = {
  uuid: "",
  order: "",
  hn: "",
  firstname: "",
  lastname: "",
  phone: "",
  underlyingDisease: "",
  prePurchaseAhi: "",
  hospitalRef: "",
  node: "",
  nodeId: "",
  customer: "",
  date: formatLocalDate(new Date()),
  time: "",
  type: "CPAP Follow-Up 1M",
  status: "CPAP Follow Up",
  doctorActionStatus: "No Doctor Action",
  technicianConsult: "กรุณาเลือก",
  doctorConsult: "กรุณาเลือก",

  latestTherapyStatus: null,
  originalTherapyStatus: null,
  latestTherapyStatusRecord: null,

  _createdBy: "",
  _createdAt: null,
  _updatedBy: "",
  _updatedAt: null,

  serialNumber: "",
  brand: "",
  model: "",
  cpapType: "Auto-CPAP",
  maskType: "กรุณาเลือก",
  maskSize: "กรุณาเลือก",

  minPressure: "",
  maxPressure: "",
  currentRampTime: "",
  epr: "",
  humidifierSystem: "",
  averageLeak: "",

  dataPeriod: "",
  dateSummary: "",
  cpapReportType: "",

  usageMoreThan4HoursPercent: "",
  averageUsageHours: "",

  averageAhi: "",
  pressure95Percent: "",

  leakRate: "",
  leakAssessment: "กรุณาเลือก",

  essScore: "",
  currentWeight: "",
  weightChange: "",

  wakeFeeling: "",
  daytimeSleepiness: "",
  sideEffects: [],
  sideEffectsOther: "",

  usageReports: [],
  questionnaires: [],
  sleepStudyReports: [],
  annualMaskPurchase: null,

  note: "",
};

const getStatusConfig = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const toDateString = (value) => {
  if (!value) return formatLocalDate(new Date());
  if (typeof value === "string") return value.slice(0, 10);
  if (value.toDate) return formatLocalDate(value.toDate());
  if (value instanceof Date) return formatLocalDate(value);
  return formatLocalDate(new Date());
};

const safeText = (value) =>
  value === undefined || value === null ? "" : value;
const formatCaseCount = (count) =>
  `${count} ${count === 1 ? "case" : "cases"}`;
const parseAverageAhi = (value) => {
  if (value === undefined || value === null) return null;

  const text = String(value).trim();

  if (!text) return null;

  // Supports values like "35", "35.2", "AHI 35", "35 events/hour"
  const number = parseFloat(text.replace(/[^\d.-]/g, ""));

  if (Number.isNaN(number)) return null;

  return number;
};
class CPAPFollowupCalendar extends Component {
  static contextType = AuthContext;

  calendarComponentRef = React.createRef();
  usageFileRef = React.createRef();
  questionnaireFileRef = React.createRef();
  cpapReportImportRef = React.createRef();
  symptomPdfImportRef = React.createRef();
  importFollowupXlsxRef = React.createRef();
  // Imported Therapy PDFs stay in browser memory until the operator saves the
  // CPAP follow-up event. Do not put File objects in the Firestore payload.
  pendingCpapUsageReports = [];

  state = {
    calendarEvents: [],
    filteredEvents: [],
    followupSearchText: "",
    nodeList: [],
    customerList: [],
    filterCustomerList: [],
    staffAdminProfile: null,
    technicianList: ["กรุณาเลือก"],
    doctorList: ["กรุณาเลือก"],
    activeTab: "patient",
    form: { ...emptyForm },
    selectedEvent: null,
    isAddEventModal: false,
    isEditEventModal: false,
    isEditMode: true,
    isNewPatient: false,
    isLoading: true,
    isUploading: false,
    isImportingCpapReport: false,
    isImportingSymptomPdf: false,

    isFilePreviewModal: false,
    previewFile: null,

    filters: {
      node: "All Node",
      customer: "All Customer",
      status: "All Status",
      type: "All Type",
      doctor: "All Doctor",
      doctorActionStatus: "All Doctor Action",
      averageAhi: "All Average AHI",
    },
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentDidUpdate() {
    if (this._followupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) {
      this._followupAccessKey = getCpapFollowupAccessKey(this.context && this.context.currentUser);
      this.setState({ isAddEventModal: false, isEditEventModal: false, selectedEvent: null, form: { ...emptyForm }, filterCustomerList: [], isFilePreviewModal: false, previewFile: null });
      this.fetchData();
    }
  }

  filterAccessibleRecords = (records) => filterCpapFollowupRecords({
    records, currentUser: this.context && this.context.currentUser,
    adminData: this.state.staffAdminProfile, nodes: this.state.nodeList,
  });

  componentWillUnmount() {
    this._isMounted = false;
  }

  getExcelValue = (row, keys, defaultValue = "") => {
    for (const key of keys) {
      if (
        row[key] !== undefined &&
        row[key] !== null &&
        String(row[key]).trim() !== ""
      ) {
        return String(row[key]).trim();
      }
    }

    return defaultValue;
  };

  getImportCustomerFromRow = (row = {}) => {
    const hospitalColumn = Object.keys(row).find(
      (key) => String(key || "").trim() === "โรงพยาบาลผู้ส่งตรวจ",
    );

    return hospitalColumn ? this.getExcelValue(row, [hospitalColumn]) : "";
  };

  normalizeImportCustomerName = (value = "") =>
    String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();

  loadImportCustomerNodeIndex = async () => {
    if (this.importCustomerNodeIndexPromise) {
      return this.importCustomerNodeIndexPromise;
    }

    const nodes = Array.isArray(this.state.nodeList)
      ? this.state.nodeList
      : [];

    this.importCustomerNodeIndexPromise = Promise.all(
      nodes.map(async (node) => {
        try {
          const customerSnap = await getDocs(
            collection(db, "nodes", node.id, "customers"),
          );

          const customers = filterCpapFollowupCustomers({
            customers: customerSnap.docs.map((row) => ({ ...row.data(), id: row.id })),
            currentUser: this.context && this.context.currentUser,
            adminData: this.state.staffAdminProfile, node,
          });
          return customers.map((customer) => ({ node, customerId: customer.id, customerData: customer }));
        } catch (error) {
          console.error("Load import customer lookup error:", error);
          return [];
        }
      }),
    ).then((customerGroups) => {
      const index = new Map();

      customerGroups.forEach((customerGroup) => {
        customerGroup.forEach(({ node, customerId, customerData }) => {
          const customer =
            customerData.CustomerNameEN ||
            customerData.customerName ||
            customerId ||
            "";

          [
            customerId,
            customerData.CustomerNameEN,
            customerData.CustomerNameTH,
            customerData.customerName,
          ].forEach((value) => {
            const key = this.normalizeImportCustomerName(value);

            if (key && !index.has(key)) {
              index.set(key, {
                nodeName: node.nodename || "",
                nodeId: node.id || "",
                customer,
              });
            }
          });
        });
      });

      return index;
    });

    return this.importCustomerNodeIndexPromise;
  };

  findImportCustomerLocation = async (customerName = "") => {
    const customerKey = this.normalizeImportCustomerName(customerName);

    if (!customerKey) {
      return { nodeName: "", nodeId: "", customer: "" };
    }

    const customerIndex = await this.loadImportCustomerNodeIndex();

    return (
      customerIndex.get(customerKey) || {
        nodeName: "",
        nodeId: "",
        customer: "",
      }
    );
  };

  parseExcelDateToLocalDate = (value) => {
    if (!value) return "";

    if (value instanceof Date && !isNaN(value.getTime())) {
      return formatLocalDate(value);
    }

    if (typeof value === "number") {
      const parsed = XLSX.SSF.parse_date_code(value);

      if (parsed) {
        return formatLocalDate(
          new Date(parsed.y, parsed.m - 1, parsed.d),
        );
      }
    }

    const text = String(value).trim();

    // Supports 5/5/2569, 05/05/2569, 5-5-2569
    const match = text.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);

    if (match) {
      const day = Number(match[1]);
      const month = Number(match[2]);
      let year = Number(match[3]);

      // Thai Buddhist year -> Christian year
      if (year > 2400) year -= 543;

      return formatLocalDate(new Date(year, month - 1, day));
    }

    const fallbackDate = new Date(text);

    if (!isNaN(fallbackDate.getTime())) {
      return formatLocalDate(fallbackDate);
    }

    return "";
  };

  loadPdfJs = () => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return Promise.resolve(window.pdfjsLib);
    }

    if (window.__cpapFollowupPdfJsLoading) {
      return window.__cpapFollowupPdfJsLoading;
    }

    window.__cpapFollowupPdfJsLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = PDFJS_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        if (!window.pdfjsLib) {
          reject(new Error("PDF parser is not available"));
          return;
        }

        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => reject(new Error("Cannot load PDF parser"));
      document.body.appendChild(script);
    });

    return window.__cpapFollowupPdfJsLoading;
  };

  loadTesseractJs = () => {
    if (window.Tesseract) {
      return Promise.resolve(window.Tesseract);
    }

    if (window.__cpapFollowupTesseractLoading) {
      return window.__cpapFollowupTesseractLoading;
    }

    window.__cpapFollowupTesseractLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TESSERACT_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        if (!window.Tesseract) {
          reject(new Error("OCR parser is not available"));
          return;
        }

        resolve(window.Tesseract);
      };
      script.onerror = () => reject(new Error("Cannot load OCR parser"));
      document.body.appendChild(script);
    });

    return window.__cpapFollowupTesseractLoading;
  };

  extractPdfPageOcrText = async (pdf) => {
    const Tesseract = await this.loadTesseractJs();
    const pageTexts = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      const canvasContext = canvas.getContext("2d");

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext,
        viewport,
      }).promise;

      const result = await Tesseract.recognize(canvas, "eng+tha");
      pageTexts.push(result && result.data ? result.data.text || "" : "");
    }

    return pageTexts.join("\n");
  };

  extractPdfTextFromFile = async (file) => {
    const pdfjsLib = await this.loadPdfJs();
    const buffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: buffer });
    const pdf = await loadingTask.promise;
    const pageTexts = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item) => item.str || "")
        .join(" ");
      pageTexts.push(pageText);
    }

    const extractedText = pageTexts.join("\n");

    if (this.normalizeReportText(extractedText).length >= 80) {
      return extractedText;
    }

    try {
      return await this.extractPdfPageOcrText(pdf);
    } catch (error) {
      console.warn("CPAP report OCR fallback failed:", error);
      return extractedText;
    }
  };

  normalizeReportText = (text = "") =>
    String(text || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t\r\n]+/g, " ")
      .trim();

  normalizeSymptomFreeText = (text = "") => {
    const normalized = this.normalizeReportText(
      String(text || "")
        .replace(/\u0000/g, "")
        // Chrome-printed Thai PDFs can expose positioned marks as PUA glyphs.
        .replace(/\uF70A/g, "\u0E48")
        .replace(/\uF70B/g, "\u0E49")
        .replace(/\uF70E/g, "\u0E4C"),
    );

    return this.normalizeFragmentedThaiText(normalized)
      .replace(/\u0E4D\u0E32/g, "\u0E33")
      .replace(/เป็\s+น/g, "เป็น")
      .replace(/\s*,\s*/g, ", ")
      .trim();
  };

  normalizeUnderlyingDisease = (value = "") =>
    this.normalizeSymptomFreeText(value)
      .replace(/^[\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]+\s*/, "")
      .trim();

  normalizeFragmentedThaiText = (text = "") =>
    String(text || "").replace(
      /([\u0E00-\u0E7F]+)\s+(?=([\u0E00-\u0E7F]+))/g,
      (match, left, right) => {
        if (left.length <= 3 || right.length <= 3) return left;
        return match;
      },
    );

  extractWeightComparisonOption = (text = "") => {
    const cleaned = this.normalizeSymptomFreeText(text);
    const option = WEIGHT_COMPARISON_OPTIONS.find((item) =>
      item.aliases.some((alias) => cleaned.includes(alias)),
    );

    return option ? option.label : "";
  };

  extractSymptomCurrentWeight = (text = "") => {
    const normalized = this.normalizeSymptomFreeText(text);
    const value = this.extractFirstMatch(normalized, [
      /น\s*[้ํ]*\s*[ำา]\s*หนัก\s*ปัจจุบัน(?:\s*เทียบกับ.{0,120}?\*)?\s*(?:Dropdown\s*)?(.+?)\s*(?:การ\s*เปล[\u0E35\u0E48]*\s*ยน\s*แปลง|เปล[\u0E35\u0E48]*\s*ยน\s*แปลงของ\s*น\s*[้ํ]*\s*[ำา]\s*หนัก|ส่วนสูง|อาการ|แบบประเมิน|https?:\/\/|$)/i,
      /Current\s*Weight(?:\s*\([^)]*\))?\s*:?\s*(.+?)\s+(?:Weight\s*Change|Height|Symptoms|Dropdown|$)/i,
    ]);

    if (!value) return "";

    const selectedOption = this.extractWeightComparisonOption(value);
    if (selectedOption) return selectedOption;

    const cleaned = this.normalizeSymptomFreeText(value)
      .replace(/^Dropdown\s*/i, "")
      .replace(/^เทียบกับ.*?\*\s*/, "")
      .replace(/^\*\s*/, "")
      .trim();

    if (/^\d{1,2}\/\d{1,2}\/\d{2,4},\s*\d{1,2}:\d{2}/i.test(cleaned)) {
      return "";
    }

    return cleaned;
  };

  normalizeWeightChangeText = (value = "") => {
    const cleaned = this.normalizeSymptomFreeText(value)
      .replace(/^Dropdown\s*/i, "")
      .replace(/^\*+\s*/, "")
      .replace(/\s*\*+$/, "")
      .trim();

    if (/^ไม่\s*(?:เพิ่ม|เพิม)\s*ไม่\s*ลด$/i.test(cleaned)) {
      return "ไม่เพิ่มไม่ลด";
    }

    return cleaned;
  };

  extractSymptomPageLeadingWeightChange = (pages = []) => {
    const weightChangeLabel =
      "(?:การ\\s*)?เปล[\\u0E35\\u0E48]*\\s*ยน\\s*แปลงของ\\s*น\\s*[้ํ]*\\s*[ำา]\\s*หนัก";
    const leadingNumberPattern = new RegExp(
      `^\\s*([+-]?\\d+(?:\\.\\d+)?(?:\\s*-\\s*\\d+(?:\\.\\d+)?)?(?:\\s*(?:กิโลกรัม|กก\\.?))?)\\s+(?=${weightChangeLabel})`,
      "i",
    );

    for (const page of Array.isArray(pages) ? pages : []) {
      const normalized = this.normalizeSymptomFreeText(page && page.text);
      const match = normalized.match(leadingNumberPattern);

      if (match && match[1]) {
        return this.normalizeWeightChangeText(match[1]);
      }
    }

    return "";
  };

  extractSymptomWeightChange = (text = "", answerFirstValue = "") => {
    const normalized = this.normalizeSymptomFreeText(text);
    const weightChangeLabel =
      "(?:การ\\s*)?เปล[\\u0E35\\u0E48]*\\s*ยน\\s*แปลงของ\\s*น\\s*[้ํ]*\\s*[ำา]\\s*หนัก";
    const nextSection =
      "แบบประเมินความง่วง|ESS|การง่วงนอนขณะ|อาการโดยรวม|ผลข้างเคียง|This form was|Google Forms|Forms|https?:\\/\\/";
    const labelFirstValue = this.extractFirstMatch(normalized, [
      new RegExp(
        `${weightChangeLabel}[^*]{0,220}\\*\\s*(.*?)(?=\\s*(?:${nextSection}|$))`,
        "i",
      ),
    ]);
    const answerAfterCurrentWeight = this.extractFirstMatch(normalized, [
      new RegExp(
        `น\\s*[้ํ]*\\s*[ำา]\\s*หนัก\\s*ปัจจุบัน\\s*\\*\\s*(.+?)\\s*(?=${weightChangeLabel})`,
        "i",
      ),
    ]);
    const candidates = [
      labelFirstValue,
      answerFirstValue,
      answerAfterCurrentWeight,
    ];

    for (let index = 0; index < candidates.length; index += 1) {
      const value = this.normalizeWeightChangeText(candidates[index]);

      if (!value || value.length > 250) continue;
      if (
        /^(?:\d{1,2}\/\d{1,2}\/\d{2,4},|แบบประเมิน|ESS|การง่วงนอนขณะ|อาการโดยรวม|ผลข้างเคียง|https?:\/\/)/i.test(
          value,
        )
      ) {
        continue;
      }
      if (index === 2 && /^[+-]?\d+(?:\.\d+)?$/.test(value)) continue;

      return value;
    }

    if (
      /(?:ไม่\s*(?:เพิ่ม|เพิม)\s*ไม่\s*ลด|ไม่\s*เปลี่ยนแปลง)/i.test(
        normalized,
      )
    ) {
      return "ไม่เพิ่มไม่ลด";
    }

    return "";
  };

  extractSymptomAnswerFirstFields = (text = "") => {
    const normalized = this.normalizeSymptomFreeText(text);
    const answerFirstWeightChange = this.normalizeSymptomFreeText(
      this.extractFirstMatch(normalized, [
        /(?:^|\*)\s*((?:เพิ่มขึ้น|ลดลง)\s+(?:ประมาณ\s*)?[+-]?\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?\s*(?:กิโลกรัม|กก\.?)?)\s*การ\s*เปล[\u0E35\u0E48]*\s*ยน\s*แปลงของ\s*น\s*[้ํ]*\s*[ำา]\s*หนัก/i,
      ]),
    );
    const rawAnswerBlock = this.extractFirstMatch(normalized, [
      /YYYY\s+(.+?)\s+แบบประเมินหลังการรักษาด้วยเคร[ื่]*อง\s*CPAP/i,
    ]);
    const answerBlock = this.normalizeSymptomFreeText(rawAnswerBlock)
      .replace(/รอบ\s*1\s*-\s*3\s*เดือน/gi, " ")
      .replace(/รอบ\s*6\s*เดือน/gi, " ")
      .replace(/รอบ\s*1\s*ปี/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (
      !answerBlock ||
      /ชื่อ-นามสกุล|วันที่ประเมิน|โรคประจ[ำา]ตัว|น\s*[้ํ]*\s*[ำา]\s*หนัก\s*ปัจจุบัน|เปล[\u0E35\u0E48]*\s*ยน\s*แปลงของ\s*น\s*[้ํ]*\s*[ำา]\s*หนัก/i.test(
        answerBlock,
      )
    ) {
      return {};
    }

    const weightChangeMatch = answerBlock.match(
      /((?:เท่าเดิม|เหมือนเดิม|ไม่\s*(?:เพิ่ม|เพิม)\s*ไม่\s*ลด|ไม่\s*เปลี่ยนแปลง)|(?:(?:เพิ่มขึ้น|ลดลง)\s*)?(?:ประมาณ\s*)?[+-]?\d+(?:\.\d+)?(?:\s*-\s*\d+(?:\.\d+)?)?\s*(?:กิโลกรัม|กก\.?))\s*$/i,
    );
    const beforeWeightChange = weightChangeMatch
      ? answerBlock.slice(0, weightChangeMatch.index).trim()
      : answerBlock;
    const currentWeightMatch = beforeWeightChange.match(
      /^(?:(.*?)\s+)?(\d+(?:\.\d+)?)$/,
    );

    if (!currentWeightMatch) return {};

    const values = {
      currentWeight: this.normalizeReportNumber(currentWeightMatch[2]),
      weightChange:
        answerFirstWeightChange ||
        (weightChangeMatch
          ? this.normalizeWeightChangeText(weightChangeMatch[1])
          : ""),
    };
    const underlyingDisease = this.normalizeUnderlyingDisease(
      currentWeightMatch[1],
    );

    if (underlyingDisease) values.underlyingDisease = underlyingDisease;

    return values;
  };

  extractFirstMatch = (text, regexList) => {
    for (const regex of regexList) {
      const match = text.match(regex);
      if (match && match[1]) {
        return String(match[1]).trim();
      }
    }

    return "";
  };

  normalizeReportNumber = (value) => {
    if (value === undefined || value === null) return "";
    const text = String(value).trim();
    const hourMinuteWordMatch = text.match(
      /(\d+(?:\.\d+)?)\s*hours?\s+(\d+(?:\.\d+)?)\s*minutes?/i,
    );

    if (hourMinuteWordMatch) {
      const hours = Number(hourMinuteWordMatch[1]);
      const minutes = Number(hourMinuteWordMatch[2]);
      const total = hours + minutes / 60;
      return Number.isFinite(total) ? total.toFixed(2) : "";
    }

    const timeMatch = text.match(/^(\d{1,2})\s*:\s*(\d{2})$/);

    if (timeMatch) {
      const hours = Number(timeMatch[1]);
      const minutes = Number(timeMatch[2]);
      const total = hours + minutes / 60;
      return Number.isFinite(total) ? total.toFixed(2) : "";
    }

    const numberMatch = text.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
    return numberMatch ? numberMatch[0] : "";
  };

  extractReportNumber = (text, regexList) =>
    this.normalizeReportNumber(this.extractFirstMatch(text, regexList));

  calculateDaysFromPeriod = (period = "") => {
    const match = String(period || "").match(
      /(\d{1,4})[\/\-. ](\d{1,2})[\/\-. ](\d{1,4})\s*(?:to|-|–|~)\s*(\d{1,4})[\/\-. ](\d{1,2})[\/\-. ](\d{1,4})/,
    );

    if (!match) return "";

    const parseDateParts = (a, b, c) => {
      let day = Number(a);
      let month = Number(b);
      let year = Number(c);

      if (String(a).length === 4) {
        year = Number(a);
        month = Number(b);
        day = Number(c);
      }

      if (year > 2400) year -= 543;
      if (year < 100) year += 2000;

      const date = new Date(year, month - 1, day);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const start = parseDateParts(match[1], match[2], match[3]);
    const end = parseDateParts(match[4], match[5], match[6]);

    if (!start || !end) return "";

    const diffDays = Math.round((end - start) / 86400000) + 1;
    return diffDays > 0 ? String(diffDays) : "";
  };

  calculatePercent = (part, total) => {
    const partNumber = Number(this.normalizeReportNumber(part));
    const totalNumber = Number(this.normalizeReportNumber(total));

    if (!Number.isFinite(partNumber) || !Number.isFinite(totalNumber)) return "";
    if (totalNumber <= 0) return "";

    return ((partNumber / totalNumber) * 100).toFixed(1);
  };

  calculateVentmedUsageMoreThan4HoursPercent = (text, totalDay) => {
    const usedOver4 = this.extractReportNumber(text, [
      /Used\s*Days\s*(?:>=|≥|>)\s*4\s*hours?\s*[:：]?\s*(\d+(?:\.\d+)?)/i,
      /Used\s*Days\s*\(?(?:>=|≥|>)\s*4\s*hrs?\)?\s*[:：]?\s*(\d+(?:\.\d+)?)/i,
    ]);

    return this.calculatePercent(usedOver4, totalDay);
  };

  extractVentmedReportedUsagePercent = (text) =>
    this.extractReportNumber(text, [
      /Used\s*Days\s*(?:>=|≥|>)\s*4\s*hours?\s+\d+(?:\.\d+)?\s+(\d+(?:\.\d+)?)\s+\d+(?:\.\d+)?(?:\s|$)/i,
    ]);

  appendDateSummaryTotalDay = (dateSummary = "", totalDay = "") => {
    const dayText = this.normalizeReportNumber(totalDay);
    const current = String(dateSummary || "").trim();

    if (!dayText) return current;

    const totalDayText = `Total Day: ${dayText}`;

    if (current.toLowerCase().includes("total day")) return current;

    return current ? `${current} | ${totalDayText}` : totalDayText;
  };

  parseCpapReportText = (rawText, reportType) => {
    const text = this.normalizeReportText(rawText);
    const reportTypeText = String(reportType || "").toLowerCase();
    const isResmed = reportTypeText.includes("resmed");
    const isVentmed = reportTypeText.includes("vent");
    const isNewVentmedReport =
      isVentmed &&
      /95%\s*Avg\.?\s*Pressure\s*[（(]\s*cmH2O\s*[）)]/i.test(text);
    const isTopson = reportTypeText.includes("topson");
    const isPhilips = reportTypeText.includes("phil");
    const values = {};

    values.dataPeriod = this.extractFirstMatch(text, [
      /Usage\s*:?\s*([0-9]{1,2}\s*\/\s*[0-9]{1,2}\s*\/\s*[0-9]{2,4}\s*(?:to|-|–|~)\s*[0-9]{1,2}\s*\/\s*[0-9]{1,2}\s*\/\s*[0-9]{2,4})/i,
      /Selected\s*Period\s*:?\s*([0-9]{1,4}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{1,4}\s*(?:to|-|–|~)\s*[0-9]{1,4}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{1,4})/i,
      /(?:Report|Usage|Data)\s*(?:period|range)\s*:?\s*([0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4}\s*(?:to|-|–|~)\s*[0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4})/i,
      /(?:Period|Range)\s*:?\s*([0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4}\s*(?:to|-|–|~)\s*[0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4})/i,
      /([0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4}\s*(?:to|-|–|~)\s*[0-9]{1,2}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{2,4})/i,
    ]);

    values.dateSummary = this.extractFirstMatch(text, [
      /(?:Date of Report|Report Date|Date)\s*:?\s*([0-9]{1,4}[\/\-. ][0-9]{1,2}[\/\-. ][0-9]{1,4})/i,
    ]);

    const totalDay = this.extractReportNumber(text, [
      /(?:Total\s*Day|Total\s*Days|Selected\s*Period\s*Days?)\s*[:：]?\s*(\d+(?:\.\d+)?)/i,
      /Usage\s*days\s*(?:\d+\s*\/\s*)?(\d+)\s*days/i,
      /Used\s*Days\s*\/\s*Total\s*Days\s*[:：]?\s*\d+\s*\/\s*(\d+)/i,
    ]);

    values.serialNumber = this.extractFirstMatch(text, [
      /(?:Serial Number|Device SN|SN|S\/N)\s*:?\s*([A-Z0-9-]{5,})/i,
    ]);

    values.model = this.extractFirstMatch(text, [
      /(?:Device model|Model)\s*:?\s*([A-Za-z0-9 +_-]{3,40})/i,
    ]);

    values.minPressure = this.extractReportNumber(text, [
      /(?:Min(?:imum)? Pressure|Min\.? Pressure|APAP Min|Min P)\D{0,30}(\d+(?:\.\d+)?)/i,
      /Set\s*pressure\D{0,30}(\d+(?:\.\d+)?)/i,
      /Pressure\s*:?\s*(\d+(?:\.\d+)?)\s*(?:-|–|to)\s*\d+(?:\.\d+)?/i,
    ]);

    values.maxPressure = this.extractReportNumber(text, [
      /(?:Max(?:imum)? Pressure|Max\.? Pressure|APAP Max|Max P)\D{0,30}(\d+(?:\.\d+)?)/i,
      /Set\s*pressure\D{0,30}(\d+(?:\.\d+)?)/i,
      /Pressure\s*:?\s*\d+(?:\.\d+)?\s*(?:-|–|to)\s*(\d+(?:\.\d+)?)/i,
    ]);

    values.currentRampTime = this.extractReportNumber(text, [
      /(?:Ramp Time|Ramp)\D{0,30}(\d+(?:\.\d+)?)/i,
    ]);

    values.humidifierSystem = this.extractFirstMatch(text, [
      /(?:Humidifier|Humidity|Humidifier System)\s*:?\s*([A-Za-z0-9 +_-]{1,30})/i,
    ]);

    values.usageMoreThan4HoursPercent = this.extractReportNumber(text, [
      /Used\s*Days\s*(?:>=|≥|>)\s*4\s*hours?\s+\d+(?:\.\d+)?\s+(\d+(?:\.\d+)?)\s*%/i,
      /Used\s*Days\s*(?:>=|≥|>)\s*4\s*hours?\s*:?\s*\d+(?:\.\d+)?\s+(\d+(?:\.\d+)?)\s*%/i,
      /%\s*of\s*Selected\s*Period\s*>=\s*4\s*hr\s*\(h\/d\)\s*:?\s*(\d+(?:\.\d+)?)\s*%?/i,
      /%\s*of\s*Selected\s*Period\s*>=\s*4\s*(?:hr|hours?)\D{0,30}(\d+(?:\.\d+)?)\s*%?/i,
      />=\s*4\s*hours\s+\d+\s*days\s*\((\d+(?:\.\d+)?)%\)/i,
      /Used\s*Days\s*\(?>\s*4\s*hrs?\)\s*\/\s*Total\s*Days\s*:?\s*\d+\s*\/\s*\d+\s*\(?(\d+(?:\.\d+)?)\s*%?\)?/i,
      /(?:>=\s*4\s*hours|>\s*4\s*h(?:ours)?|Usage\s*>\s*4\s*h(?:ours)?|Days\s*>=\s*4\s*hours|4\s*hours\s*compliance)\D{0,50}(\d+(?:\.\d+)?)\s*%?/i,
      /(\d+(?:\.\d+)?)\s*%\s*(?:>=\s*4\s*hours|>\s*4\s*h(?:ours)?|4\s*hours)/i,
    ]);

    values.averageUsageHours = this.extractReportNumber(text, [
      /(?:Average Usage|Avg\.?\s*Usage|Average daily usage|Average use|Usage average|Avg daily use|Average Connect Time|Avg\.?\s*Duration)\D{0,50}(\d+\s*hours?\s+\d+\s*minutes?|\d{1,2}\s*:\s*\d{2}|\d+(?:\.\d+)?)/i,
      /(?:Used Days Average|Average usage \(days used\)|Average usage \(total days\)|Average Used Hours(?:\(>\s*4\s*hrs?\))?)\D{0,50}(\d+\s*hours?\s+\d+\s*minutes?|\d{1,2}\s*:\s*\d{2}|\d+(?:\.\d+)?)/i,
    ]);

    values.averageAhi = this.extractReportNumber(text, [
      /(?:Average\s*)?AHI(?:\s*Index)?\D{0,40}(\d+(?:\.\d+)?)/i,
      /Avg\.?\s*AHI\D{0,40}(\d+(?:\.\d+)?)/i,
      /Apnea[- ]Hypopnea Index\D{0,40}(\d+(?:\.\d+)?)/i,
    ]);

    values.pressure95Percent = this.extractReportNumber(text, [
      /95%\s*Avg\.?\s*Pressure\s*(?:[:：]\s*)?(?:[（(]\s*cmH2O\s*[）)]\s*)?(\d+(?:\.\d+)?)/i,
      /95%\s*Avg\.?\s*Pressure\D{0,40}(\d+(?:\.\d+)?)/i,
      /(?:95(?:th)? Percentile Pressure|Pressure 95(?:th)?|95%\s*Pressure|P95(?:\s*Pressure)?|95th\s*%ile\s*Pressure)\D{0,40}(\d+(?:\.\d+)?)/i,
      /Pressure\s*\(cmH2O\).{0,300}?95th\s*%\s*\(avg\)\s*(\d+(?:\.\d+)?)/i,
      /Pressure.{0,100}?95th\s*percentile\s*:?\s*(\d+(?:\.\d+)?)/i,
      /Pressure\D{0,20}95(?:th|%)\D{0,20}(\d+(?:\.\d+)?)/i,
    ]);

    values.averageLeak = this.extractReportNumber(text, [
      /Leaks?\s*-\s*L\/min.{0,140}?95th\s*percentile\s*:?\s*(\d+(?:\.\d+)?)/i,
      /Leak\s*\(L\/min\).{0,140}?95th\s*%\s*\(avg\)\s*(\d+(?:\.\d+)?)/i,
      /(?:Average Leak|Avg\.?\s*Leak|Leak Average|Median Leak)\D{0,40}(\d+(?:\.\d+)?)/i,
      /(?:Leak)\D{0,20}(?:Median|Average)\D{0,20}(\d+(?:\.\d+)?)/i,
      /Leak\s*90\s*[:：]?\s*(\d+(?:\.\d+)?)\s*l\s*\/?\s*min/i,
      /Leak90\s*[:：]?\s*(\d+(?:\.\d+)?)\s*l\s*\/?\s*min/i,
    ]);

    values.leakRate = getDefaultLeakRateForCpapReportType(reportType);

    if (isResmed && !values.pressure95Percent) {
      values.pressure95Percent = this.extractReportNumber(text, [
        /Pressure\D{0,60}95th\D{0,20}(\d+(?:\.\d+)?)/i,
      ]);
    }

    if (isTopson && !values.pressure95Percent) {
      values.pressure95Percent = this.extractReportNumber(text, [
        /P90\s*[:：]?\s*(\d+(?:\.\d+)?)\s*cmH2O/i,
        /Average\s*Pressure.{0,80}?P90\s*[:：]?\s*(\d+(?:\.\d+)?)/i,
      ]);
    }

    if (isPhilips && !values.pressure95Percent) {
      values.pressure95Percent = this.extractReportNumber(text, [
        /Average\s*Device\s*Pressure\s*(?:<=|≤)\s*90%\s*of\s*time\s*(\d+(?:\.\d+)?)/i,
        /CPAP\s*Pressure\D{0,30}(\d+(?:\.\d+)?)/i,
      ]);
    }

    values.cpapReportType = reportType;

    const resolvedTotalDay =
      totalDay || this.calculateDaysFromPeriod(values.dataPeriod);

    values.dateSummary = this.appendDateSummaryTotalDay(
      values.dateSummary,
      resolvedTotalDay,
    );

    const reportedVentmedUsagePercent = isNewVentmedReport
      ? this.extractVentmedReportedUsagePercent(text)
      : "";

    const calculatedVentmedUsagePercent =
      isVentmed && !reportedVentmedUsagePercent && resolvedTotalDay
        ? this.calculateVentmedUsageMoreThan4HoursPercent(text, resolvedTotalDay)
        : "";

    if (reportedVentmedUsagePercent) {
      values.usageMoreThan4HoursPercent = reportedVentmedUsagePercent;
    } else if (calculatedVentmedUsagePercent) {
      values.usageMoreThan4HoursPercent = calculatedVentmedUsagePercent;
    } else if (!values.usageMoreThan4HoursPercent) {
      const usedOver4 = this.extractReportNumber(text, [
        /Used\s*Days\s*>\s*4\s*hours\s*:?\s*(\d+(?:\.\d+)?)/i,
        /Used\s*Days\s*(?:>=|≥)\s*4\s*hours\s*[:：]?\s*(\d+(?:\.\d+)?)/i,
        /Used\s*Days\s*\(>\s*4\s*hrs?\)\s*:?\s*(\d+(?:\.\d+)?)/i,
        />=\s*4\s*hours\s+(\d+)\s*days/i,
      ]);

      values.usageMoreThan4HoursPercent = this.calculatePercent(
        usedOver4,
        resolvedTotalDay,
      );
    }

    Object.keys(values).forEach((key) => {
      if (values[key] === "") delete values[key];
    });

    return values;
  };

  handleCpapReportImportClick = () => {
    if (!this.state.isEditMode) {
      notifyWarning("Please click edit before import PDF");
      return;
    }

    if (!this.state.form.hn) {
      notifyWarning("HN is empty");
      return;
    }

    if (this.cpapReportImportRef.current) {
      this.cpapReportImportRef.current.click();
    }
  };

  queueCpapUsageReport = (file) => {
    const queuedAt = new Date().toISOString();
    const pendingFile = {
      id: `pending-cpap-${Date.now()}-${this.pendingCpapUsageReports.length}`,
      fileName: file.name || "CPAP Usage Report.pdf",
      contentType: file.type || "application/pdf",
      size: file.size || 0,
      localFile: file,
      queuedAt,
      pendingUpload: true,
      followupUuid: this.state.form.uuid || "",
      hn: this.state.form.hn || "",
    };

    this.pendingCpapUsageReports = [
      ...this.pendingCpapUsageReports,
      pendingFile,
    ];

    return pendingFile;
  };

  removePendingCpapUsageReport = (pendingId) => {
    this.pendingCpapUsageReports = this.pendingCpapUsageReports.filter(
      (file) => file.id !== pendingId,
    );

    // Pending File objects deliberately stay outside form state so a Firestore
    // save never receives a browser File. Copy the form only to refresh this UI.
    this.setState((previousState) => ({
      form: { ...previousState.form },
    }));
  };

  handleCpapReportFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      notifyWarning("Please upload PDF report file");
      event.target.value = "";
      return;
    }

    try {
      this.setState({ isImportingCpapReport: true });
      const reportType =
        this.state.form.cpapReportType || this.inferCpapReportType();
      const text = await this.extractPdfTextFromFile(file);

      if (!this.normalizeReportText(text)) {
        notifyWarning(
          "Cannot read text from this PDF. It may be scanned/image PDF.",
        );
        return;
      }

      const parsedValues = this.parseCpapReportText(text, reportType);
      const importableValues = Object.keys(parsedValues).reduce((acc, key) => {
        if (parsedValues[key] !== "") acc[key] = parsedValues[key];
        return acc;
      }, {});

      const filledCount = Object.keys(importableValues).filter(
        (key) => key !== "cpapReportType",
      ).length;

      const nextForm = {
        ...this.state.form,
        ...importableValues,
      };

      this.queueCpapUsageReport(file);
      this.setState({ form: nextForm });

      if (filledCount === 0) {
        notifyWarning(
          "PDF was added to CPAP Usage Report and is waiting to save, but no therapy fields matched.",
        );
        return;
      }

      notifyTopRight(
        `Imported ${filledCount} therapy field(s). CPAP Usage Report is waiting to save.`,
      );
    } catch (error) {
      console.error(error);
      notifyError("Cannot import CPAP report PDF");
    } finally {
      this.setState({ isImportingCpapReport: false });
      if (event.target) event.target.value = "";
    }
  };

  mergeSelectionComponents = (components) => {
    const clusters = [];

    components.forEach((component) => {
      const target = clusters.find((cluster) => {
        const dx = cluster.x - component.x;
        const dy = cluster.y - component.y;
        return Math.sqrt(dx * dx + dy * dy) < 26;
      });

      if (!target) {
        clusters.push({ ...component });
        return;
      }

      const total = target.count + component.count;
      target.x = (target.x * target.count + component.x * component.count) / total;
      target.y = (target.y * target.count + component.y * component.count) / total;
      target.count = total;
      target.minX = Math.min(target.minX, component.minX);
      target.minY = Math.min(target.minY, component.minY);
      target.maxX = Math.max(target.maxX, component.maxX);
      target.maxY = Math.max(target.maxY, component.maxY);
    });

    return clusters;
  };

  detectPurpleSelectionMarkers = (canvas) => {
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return [];

    const { width, height } = canvas;
    const imageData = context.getImageData(0, 0, width, height);
    const { data } = imageData;
    const visited = new Uint8Array(width * height);
    const components = [];

    const isPurplePixel = (pixelIndex) => {
      const offset = pixelIndex * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const a = data[offset + 3];

      // Copied Google Forms can use magenta-purple (BMH) instead of the
      // original blue-purple (LPH). Both must be distinct from red required
      // marks, gray unselected controls, and white page backgrounds.
      return a > 80 && r > 70 && b > 120 && b > r - 35 &&
        r > g + 15 && b > g + 35;
    };

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = y * width + x;

        if (visited[index] || !isPurplePixel(index)) continue;

        const stack = [index];
        visited[index] = 1;

        let count = 0;
        let minX = x;
        let minY = y;
        let maxX = x;
        let maxY = y;
        let sumX = 0;
        let sumY = 0;

        while (stack.length) {
          const current = stack.pop();
          const currentX = current % width;
          const currentY = Math.floor(current / width);

          count += 1;
          sumX += currentX;
          sumY += currentY;
          minX = Math.min(minX, currentX);
          minY = Math.min(minY, currentY);
          maxX = Math.max(maxX, currentX);
          maxY = Math.max(maxY, currentY);

          for (let nextY = currentY - 1; nextY <= currentY + 1; nextY += 1) {
            if (nextY < 0 || nextY >= height) continue;

            for (let nextX = currentX - 1; nextX <= currentX + 1; nextX += 1) {
              if (nextX < 0 || nextX >= width) continue;

              const nextIndex = nextY * width + nextX;
              if (visited[nextIndex] || !isPurplePixel(nextIndex)) continue;

              visited[nextIndex] = 1;
              stack.push(nextIndex);
            }
          }
        }

        if (count > 20) {
          components.push({
            count,
            minX,
            minY,
            maxX,
            maxY,
            x: sumX / count,
            y: sumY / count,
          });
        }
      }
    }

    return this.mergeSelectionComponents(components).sort((a, b) =>
      a.y === b.y ? a.x - b.x : a.y - b.y,
    );
  };

  buildPdfTextLines = (items = [], viewport, pdfjsLib) => {
    if (!viewport) return [];

    const positionedItems = (Array.isArray(items) ? items : [])
      .map((item) => {
        const text = String((item && item.str) || "").trim();
        const itemTransform = item && item.transform;

        if (!text || !itemTransform || itemTransform.length < 6) return null;

        let transformed = null;

        if (
          pdfjsLib &&
          pdfjsLib.Util &&
          typeof pdfjsLib.Util.transform === "function" &&
          viewport.transform &&
          viewport.transform.length >= 6
        ) {
          transformed = pdfjsLib.Util.transform(
            viewport.transform,
            itemTransform,
          );
        } else if (typeof viewport.convertToViewportPoint === "function") {
          const point = viewport.convertToViewportPoint(
            itemTransform[4],
            itemTransform[5],
          );
          transformed = [1, 0, 0, 1, point[0], point[1]];
        }

        if (!transformed) return null;

        const height = Math.max(
          Math.abs(transformed[0] || 0),
          Math.abs(transformed[1] || 0),
          Math.abs(transformed[2] || 0),
          Math.abs(transformed[3] || 0),
          1,
        );

        return {
          text,
          x: transformed[4],
          y: transformed[5],
          height,
        };
      })
      .filter((item) => item)
      .sort((left, right) =>
        left.y === right.y ? left.x - right.x : left.y - right.y,
      );

    const lines = [];

    positionedItems.forEach((item) => {
      const currentLine = lines[lines.length - 1];
      const lineTolerance = currentLine
        ? Math.max(
            2,
            Math.min(10, Math.max(currentLine.height, item.height) * 0.5),
          )
        : 0;

      if (!currentLine || Math.abs(currentLine.y - item.y) > lineTolerance) {
        lines.push({
          items: [item],
          y: item.y,
          height: item.height,
        });
        return;
      }

      currentLine.items.push(item);
      currentLine.y =
        currentLine.items.reduce((total, lineItem) => total + lineItem.y, 0) /
        currentLine.items.length;
      currentLine.height = Math.max(currentLine.height, item.height);
    });

    return lines.map((line) => {
      const sortedItems = line.items.sort((left, right) => left.x - right.x);

      return {
        text: sortedItems.map((item) => item.text).join(" "),
        x: sortedItems[0].x,
        y: line.y,
        height: line.height,
      };
    });
  };

  extractSymptomPdfDataFromFile = async (file) => {
    const pdfjsLib = await this.loadPdfJs();
    const buffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: buffer });
    const pdf = await loadingTask.promise;
    const pages = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const text = textContent.items.map((item) => item.str || "").join(" ");
      const viewport = page.getViewport({ scale: 2 });
      const textLines = this.buildPdfTextLines(
        textContent.items,
        viewport,
        pdfjsLib,
      );
      const canvas = document.createElement("canvas");
      const canvasContext = canvas.getContext("2d", {
        willReadFrequently: true,
      });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext,
        viewport,
      }).promise;

      pages.push({
        text,
        width: canvas.width,
        height: canvas.height,
        textLines,
        markers: this.detectPurpleSelectionMarkers(canvas),
      });
    }

    return {
      text: pages.map((page) => page.text).join("\n"),
      pages,
    };
  };

  nearestIndex = (value, options) =>
    options.reduce((bestIndex, option, index) => {
      const bestDistance = Math.abs(value - options[bestIndex]);
      const nextDistance = Math.abs(value - option);
      return nextDistance < bestDistance ? index : bestIndex;
    }, 0);

  findCpapSideEffectLabelRows = (page, template) => {
    const textLines = Array.isArray(page && page.textLines)
      ? page.textLines
          .filter(
            (line) =>
              line && Number.isFinite(line.y) && String(line.text || "").trim(),
          )
          .sort((left, right) => left.y - right.y)
      : [];

    if (!textLines.length || !template) return [];

    const pageHeight = Number(page.height) || 1000;
    const maxWrappedLineGap = pageHeight * 0.035;
    const rows = [];

    template.optionDefinitions.forEach((definition) => {
      const candidates = [];

      for (let lineIndex = 0; lineIndex < textLines.length; lineIndex += 1) {
        const combinedLines = [];

        for (
          let span = 0;
          span < 3 && lineIndex + span < textLines.length;
          span += 1
        ) {
          const line = textLines[lineIndex + span];
          const previousLine = combinedLines[combinedLines.length - 1];

          if (
            previousLine &&
            Math.abs(line.y - previousLine.y) > maxWrappedLineGap
          ) {
            break;
          }

          combinedLines.push(line);
          const combinedText = combinedLines
            .map((lineItem) => lineItem.text)
            .join(" ");

          if (matchesCpapSideEffectOptionText(combinedText, definition)) {
            candidates.push({
              value: definition.value,
              y:
                combinedLines.reduce(
                  (total, lineItem) => total + lineItem.y,
                  0,
                ) / combinedLines.length,
              span: combinedLines.length,
            });
          }
        }
      }

      if (!candidates.length) return;

      const shortestSpan = Math.min(
        ...candidates.map((candidate) => candidate.span),
      );

      candidates
        .filter((candidate) => candidate.span === shortestSpan)
        .forEach((candidate) => {
          const duplicate = rows.some(
            (row) =>
              row.value === candidate.value &&
              Math.abs(row.y - candidate.y) < 4,
          );

          if (!duplicate) rows.push(candidate);
        });
    });

    return rows.sort((left, right) => left.y - right.y);
  };

  readCpapSideEffectsFromLabelRows = (page, template) => {
    const labelRows = this.findCpapSideEffectLabelRows(page, template);

    if (!labelRows.length) {
      return { usedLabelRows: false, sideEffects: [] };
    }

    const pageWidth = Number(page.width) || 1000;
    const pageHeight = Number(page.height) || 1000;
    const rowGaps = labelRows
      .slice(1)
      .map((row, index) => row.y - labelRows[index].y)
      .filter((gap) => gap > 0)
      .sort((left, right) => left - right);
    const medianRowGap = rowGaps.length
      ? rowGaps[Math.floor(rowGaps.length / 2)]
      : pageHeight * 0.05;
    const maximumDistance = Math.max(
      14,
      Math.min(pageHeight * 0.035, medianRowGap * 0.48),
    );
    const selectedSideEffects = [];

    (Array.isArray(page.markers) ? page.markers : [])
      .filter((marker) => marker.x / pageWidth < 0.25)
      .forEach((marker) => {
        const nearestRow = labelRows.reduce((bestRow, row) =>
          Math.abs(marker.y - row.y) < Math.abs(marker.y - bestRow.y)
            ? row
            : bestRow,
        );

        if (Math.abs(marker.y - nearestRow.y) <= maximumDistance) {
          selectedSideEffects.push(nearestRow.value);
        }
      });

    return {
      usedLabelRows: true,
      sideEffects: Array.from(new Set(selectedSideEffects)),
    };
  };

  extractSymptomOtherText = (text) => {
    const normalized = this.normalizeSymptomFreeText(text);
    const footerBoundary =
      "This form was|Google Forms|F\\s*o\\s*r\\s*m\\s*s|Forms|https?:\\/\\/|แบบประเมินหลังการรักษา";
    const sectionBoundary =
      "อาการโดยรวม|Overr?all Symptoms|อาการง่วงนอนตอนกลางวัน|ผลข้างเคียงจากการใช้\\s*CPAP|ปัญหาและ(?:ผล)?ข้างเคียงที่พบ|Problems\\s*&\\s*Side Effects|การประเมินและแผนการดูแล";
    const otherLabel = "อื?่?น?ๆ";
    const answerBoundary = `${footerBoundary}|${sectionBoundary}`;
    const sideEffectBoundary = `${answerBoundary}|${otherLabel}`;
    const isValidOtherValue = (value = "") => {
      const cleaned = this.normalizeSymptomFreeText(value);
      if (!cleaned || cleaned.length <= 2 || /^[-–—]+$/.test(cleaned)) {
        return false;
      }

      return !new RegExp(
        `^(?:${answerBoundary}|\\d{1,2}\\/\\d{1,2}\\/\\d{2,4})`,
        "i",
      ).test(cleaned);
    };

    const patterns = [
      new RegExp(
        `ไม่มีผลข้างเคียง\\s*(.+?)(?=\\s+(?:${sideEffectBoundary})|$)`,
        "i",
      ),
      new RegExp(
        `${otherLabel}\\s*(.+?)(?=\\s+(?:${answerBoundary})|$)`,
        "i",
      ),
    ];

    for (const pattern of patterns) {
      const match = normalized.match(pattern);
      if (match && match[1]) {
        const value = this.normalizeSymptomFreeText(match[1]);
        if (isValidOtherValue(value)) return value;
      }
    }

    return "";
  };

  extractSymptomPositionedFields = (pages = []) => {
    const values = {};
    const definitions = [
      { key: "underlyingDisease", label: /^โรคประจำตัว\*?$/ },
      { key: "currentWeight", label: /^น[้ํ]*[ำา]หนักปัจจุบัน/ },
      { key: "weightChange", label: /^(?:การ)?เปล[ีื่]*ยนแปลงของน[้ํ]*[ำา]หนัก/ },
    ];

    // Google Forms copies can emit answers before labels in the PDF stream.
    // Use the visible same-page label/answer order for these free-text fields.
    (Array.isArray(pages) ? pages : []).forEach((page) => {
      const lines = (Array.isArray(page.textLines) ? page.textLines : [])
        .filter((line) => line && Number.isFinite(line.y))
        .map((line) => ({ ...line, text: this.normalizeSymptomFreeText(line.text) }))
        .filter((line) => line.text)
        .sort((left, right) => left.y - right.y);

      lines.forEach((line, index) => {
        const compactLabel = line.text.replace(/\s+/g, "");
        const definition = definitions.find((item) => item.label.test(compactLabel));
        const answer = lines[index + 1];
        if (!definition || !answer || values[definition.key]) return;
        if (answer.y - line.y > (Number(page.height) || 1000) * 0.1) return;
        if (/https?:\/\/|^\d{1,2}\/\d{1,2}\/\d{2,4},|แบบประเมิน|Dropdown/i.test(answer.text)) return;
        if (definitions.some((item) => item.label.test(answer.text.replace(/\s+/g, "")))) return;

        if (definition.key === "currentWeight") {
          const option = this.extractWeightComparisonOption(answer.text);
          if (option) values.currentWeight = option;
          else if (/^\d+(?:\.\d+)?(?:\s*(?:kg|กก\.?|กิโลกรัม))?$/i.test(answer.text)) {
            values.currentWeight = answer.text;
          }
        } else if (definition.key === "weightChange") {
          if (/^(?:เพิ่ม|ลด|ไม่|เท่า|เหมือน|[+\-\d])/.test(answer.text)) {
            values.weightChange = this.normalizeWeightChangeText(answer.text);
          }
        } else {
          values.underlyingDisease = this.normalizeUnderlyingDisease(answer.text);
        }
      });
    });

    return values;
  };

  parseSymptomAssessmentPdf = ({ text, pages }) => {
    const values = {};
    const pdfPages = Array.isArray(pages) ? pages : [];
    const normalizedText = this.normalizeSymptomFreeText(text);
    const answerFirstValues =
      this.extractSymptomAnswerFirstFields(normalizedText);
    const optionColumnRatios = [0.34, 0.506, 0.673, 0.839];
    const getOptionByMarker = (marker, page, options) =>
      options[
        this.nearestIndex(marker.x / page.width, optionColumnRatios)
      ] || "";

    const underlyingDisease = this.extractFirstMatch(normalizedText, [
      /โรคประจ[ำา]ตัว\s*(.+?)\s*น\s*[้ํ]*\s*[ำา]\s*หนัก\s*ปัจจุบัน/i,
      /YYYY\s+(.+?)\s+Dropdown/i,
    ]);
    if (underlyingDisease && !/dropdown/i.test(underlyingDisease)) {
      values.underlyingDisease =
        this.normalizeUnderlyingDisease(underlyingDisease);
    } else if (answerFirstValues.underlyingDisease) {
      values.underlyingDisease = answerFirstValues.underlyingDisease;
    }

    const currentWeight =
      answerFirstValues.currentWeight ||
      this.extractSymptomCurrentWeight(normalizedText);
    if (currentWeight) {
      values.currentWeight = currentWeight;
    }

    const weightChange = this.extractSymptomWeightChange(
      normalizedText,
      answerFirstValues.weightChange ||
        this.extractSymptomPageLeadingWeightChange(pdfPages),
    );
    if (weightChange !== "") values.weightChange = weightChange;

    const sideEffectsOther = this.extractSymptomOtherText(normalizedText);
    if (sideEffectsOther) values.sideEffectsOther = sideEffectsOther;

    Object.assign(values, this.extractSymptomPositionedFields(pdfPages));

    let hasWakeFeelingMarker = false;
    const essPage =
      pdfPages.find((page) => /ESS|แบบประเมินความง่วง/i.test(page.text)) ||
      pdfPages[1];

    if (essPage) {
      const markers = essPage.markers || [];
      const essMarkers = markers
        .filter((marker) => {
          const xRatio = marker.x / essPage.width;
          const yRatio = marker.y / essPage.height;
          return xRatio > 0.25 && yRatio > 0.12 && yRatio < 0.95;
        })
        .sort((a, b) => a.y - b.y)
        .slice(0, 8);

      if (essMarkers.length === 8) {
        const essScore = essMarkers.reduce((total, marker) => {
          const score = this.nearestIndex(
            marker.x / essPage.width,
            optionColumnRatios,
          );
          return total + score;
        }, 0);

        values.essScore = String(essScore);
      }

      const wakeMarker = markers
        .filter((marker) => {
          const xRatio = marker.x / essPage.width;
          const yRatio = marker.y / essPage.height;
          return xRatio > 0.25 && yRatio > 0.12 && yRatio < 0.95;
        })
        .sort((a, b) => a.y - b.y)[8];

      if (wakeMarker) {
        values.wakeFeeling = getOptionByMarker(
          wakeMarker,
          essPage,
          WAKE_FEELING_OPTIONS,
        );
        hasWakeFeelingMarker = true;
      }
    }

    const symptomPage =
      pdfPages.find((page) => {
        const templateMatch = matchCpapSideEffectPdfTemplate(page.text);
        return (
          templateMatch.titleMatched || templateMatch.matchedOptionCount >= 2
        );
      }) || pdfPages[2];

    if (symptomPage) {
      const markers = symptomPage.markers || [];
      const overallMarkers = markers
        .filter((marker) => {
          const xRatio = marker.x / symptomPage.width;
          const yRatio = marker.y / symptomPage.height;
          return xRatio > 0.25 && yRatio < 0.5;
        })
        .sort((a, b) => a.y - b.y);
      const hasWakeAndDaytimeOnPage = overallMarkers.length >= 2;

      if (!hasWakeFeelingMarker && hasWakeAndDaytimeOnPage) {
        values.wakeFeeling = getOptionByMarker(
          overallMarkers[0],
          symptomPage,
          WAKE_FEELING_OPTIONS,
        );
      }

      const daytimeMarker = overallMarkers[
        hasWakeAndDaytimeOnPage ? 1 : 0
      ];

      if (daytimeMarker) {
        values.daytimeSleepiness = getOptionByMarker(
          daytimeMarker,
          symptomPage,
          DAYTIME_SLEEPINESS_OPTIONS,
        );
      }

      const sideEffectTemplate = matchCpapSideEffectPdfTemplate(
        symptomPage.text,
      );
      const labelRowResult = this.readCpapSideEffectsFromLabelRows(
        symptomPage,
        sideEffectTemplate,
      );
      let selectedSideEffects = labelRowResult.sideEffects;

      if (!labelRowResult.usedLabelRows) {
        const firstRowRatio = hasWakeAndDaytimeOnPage ? 0.505 : 0.306;
        const rowStep = hasWakeAndDaytimeOnPage ? 0.032 : 0.0325;
        const sideEffectRowRatios = sideEffectTemplate.options.map(
          (option, index) => firstRowRatio + rowStep * index,
        );
        const sideEffectMarkerRange = [
          firstRowRatio - rowStep * 1.2,
          sideEffectRowRatios[sideEffectRowRatios.length - 1] + rowStep * 1.2,
        ];

        selectedSideEffects = markers
          .filter((marker) => {
            const xRatio = marker.x / symptomPage.width;
            const yRatio = marker.y / symptomPage.height;
            return (
              xRatio < 0.18 &&
              yRatio > sideEffectMarkerRange[0] &&
              yRatio < sideEffectMarkerRange[1]
            );
          })
          .map((marker) => {
            const index = this.nearestIndex(
              marker.y / symptomPage.height,
              sideEffectRowRatios,
            );

            return sideEffectTemplate.options[index] || "";
          })
          .filter((option) => option);
      }

      if (selectedSideEffects.length > 0) {
        values.sideEffects = Array.from(new Set(selectedSideEffects));
      }
    }

    return values;
  };

  handleSymptomPdfImportClick = () => {
    if (!this.state.isEditMode) {
      notifyWarning("Please click edit before import PDF");
      return;
    }

    if (!this.state.form.uuid) {
      notifyWarning(
        "Please save the follow-up record before import symptom PDF",
      );
      return;
    }

    if (!this.state.form.hn) {
      notifyWarning("HN is empty");
      return;
    }

    if (this.symptomPdfImportRef.current) {
      this.symptomPdfImportRef.current.click();
    }
  };

  handleSymptomPdfFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      notifyWarning("Please upload PDF file");
      event.target.value = "";
      return;
    }

    try {
      this.setState({ isImportingSymptomPdf: true });

      const pdfData = await this.extractSymptomPdfDataFromFile(file);
      const parsedValues = this.parseSymptomAssessmentPdf(pdfData);
      const importableValues = Object.keys(parsedValues).reduce((acc, key) => {
        const value = parsedValues[key];
        if (Array.isArray(value) ? value.length > 0 : value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});

      const filledCount = Object.keys(importableValues).length;

      const nextForm = {
        ...this.state.form,
        ...(filledCount > 0 ? SYMPTOM_IMPORT_DEFAULTS : {}),
        ...importableValues,
      };

      if (filledCount > 0) {
        this.setState({ form: nextForm });
      }

      const uploadResult = await this.uploadCpapFollowupFiles({
        fileType: "questionnaires",
        files: [file],
        form: nextForm,
        inputRef: this.symptomPdfImportRef,
        showSuccessAlert: false,
      });

      if (filledCount === 0) {
        notifyWarning(
          uploadResult
            ? "Uploaded questionnaire file, but no symptom fields matched"
            : "PDF was readable but no symptom fields matched",
        );
        return;
      }

      if (uploadResult) {
        notifyTopRight(
          `Imported ${filledCount} symptom field(s) and uploaded questionnaire file`,
        );
      } else {
        notifyWarning(
          `Imported ${filledCount} symptom field(s), but questionnaire file was not uploaded`,
        );
      }
    } catch (error) {
      console.error(error);
      notifyError("Cannot import symptom PDF");
    } finally {
      this.setState({ isImportingSymptomPdf: false });
      if (event.target) event.target.value = "";
    }
  };

  inferCpapReportType = () => {
    const { brand, model } = this.state.form;
    const brandText = String(brand || "").toLowerCase();
    const modelText = String(model || "").toLowerCase();

    if (modelText.includes("airmini")) return "Resmed - AirMini";

    if (brandText.includes("resmed") || modelText.includes("airsense")) {
      if (modelText.includes("11")) return "Resmed - Airsense 11";
      if (modelText.includes("airstart")) return "Resmed - Airstart 10";
      return "Resmed - Airsense 10";
    }

    if (brandText.includes("vent")) return "Ventmed";
    if (brandText.includes("topson")) return "Topson";
    if (brandText.includes("phil")) return "Phillips";
    if (brandText.includes("apex")) return "Apex";

    return "Apex";
  };

  getMaskTypeFromMaskText = (maskText = "") => {
    const text = String(maskText || "").toLowerCase();

    if (text.includes("airtouch") && text.includes("f20")) {
      return "Full Face Mask AirTouch F20";
    }
    if (text.includes("airfit") && text.includes("f20")) {
      return "Full Face Mask AirFit F20";
    }
    if (text.includes("airfit") && text.includes("n30i")) {
      return "Nasal Mask AirFit N30i";
    }
    if (text.includes("airfit") && text.includes("n30")) {
      return "Nasal Mask AirFit N30";
    }
    if (text.includes("airfit") && text.includes("n20")) {
      return "Nasal Mask AirFit N20";
    }
    if (text.includes("airfit") && text.includes("p30i")) {
      return "Nasal Pillow AirFit P30i";
    }
    if (text.includes("airfit") && text.includes("p10")) {
      return "Nasal Pillow AirFit P10";
    }
    if (text.includes("rescomf") && text.includes("full")) {
      return "Full Face Mask ResComf";
    }
    if (text.includes("rescomf")) {
      return "Nasal Mask ResComf";
    }
    if (text.includes("wizard 320")) {
      return "Full Face Mask Wizard 320";
    }
    if (text.includes("wizard 310")) {
      return "Nasal Mask Wizard 310";
    }
    if (text.includes("fm50")) {
      return "Topson Full Face FM50 Mask";
    }
    if (text.includes("nm40")) {
      return "Topson Nasal NM40 Mask";
    }
    if (text.includes("nm50")) {
      return "Topson Nasal NM50 Mask";
    }
    if (text.includes("wisp")) {
      return "Philips Wisp Nasal Mask";
    }
    if (text.includes("pico")) {
      return "Philips Pico Nasal Mask";
    }
    if (text.includes("amara")) {
      return "Philips Amara Gel Full Face Mask";
    }
    if (text.includes("nasal pillow")) return "Nasal Pillow";
    if (text.includes("full face")) return "Full Face Mask";
    if (text.includes("oronasal")) return "Oronasal Mask";
    if (text.includes("nasal mask") || text.includes("nasal")) {
      return "Nasal Mask";
    }

    return "Other";
  };

  normalizeCpapType = (type = "") => {
    const text = String(type || "").trim().toLowerCase();

    if (text === "apap" || text === "auto cpap" || text === "auto-cpap") {
      return "Auto-CPAP";
    }

    if (text === "bipap" || text === "bi-pap") {
      return "BiPAP";
    }

    if (text === "cpap") {
      return "CPAP";
    }

    return type || "Auto-CPAP";
  };

  loadProductBySerialNumber = async (serialNumber) => {
    const result = {
      brand: "",
      model: "",
      cpapType: "",
    };

    if (!serialNumber) return result;

    try {
      const productQ = query(
        collection(db, "products"),
        where("serialNumber", "==", serialNumber),
      );

      const productSnap = await getDocs(productQ);

      let productData = null;

      productSnap.forEach((item) => {
        if (!productData) productData = item.data();
      });

      if (!productData) return result;

      return {
        brand: productData.brand || "",
        model: productData.model || "",
        cpapType: this.normalizeCpapType(productData.type),
      };
    } catch (error) {
      console.error("Load product by serial number error:", error);
      return result;
    }
  };

  loadPatientInfoForImport = async (hn) => {
    const result = {
      patientData: null,
      latestEp: {},
      nodeName: "",
      latestTherapyStatus: null,
      sleepStudyReports: [],
    };

    if (!hn) return result;

    try {
      const patientQ = query(collection(db, "patients"), where("hn", "==", hn));

      const bookingQ = query(
        collectionGroup(db, "bookings"),
        where("extendedProps.hn", "==", hn),
      );

      const [patientSnap, bookingSnap] = await Promise.all([
        getDocs(patientQ),
        getDocs(bookingQ),
      ]);

      let patientData = null;

      patientSnap.forEach((item) => {
        if (!patientData) patientData = item.data();
      });

      const bookingList = [];

      bookingSnap.forEach((item) => {
        if (this.filterAccessibleRecords([item.data()]).length) bookingList.push(item.data());
      });

      bookingList.sort((a, b) => {
        const aCreated = this.getLatestTimestampSeconds(a);
        const bCreated = this.getLatestTimestampSeconds(b);
        return bCreated - aCreated;
      });

      const latestBooking = bookingList[0] || null;
      const latestEp =
        latestBooking && latestBooking.extendedProps
          ? latestBooking.extendedProps
          : {};

      const nodeName =
        (patientData && patientData.nodeRef) || latestEp.node || "";
      const patientScope = { node: nodeName, customer: (patientData && (patientData.refCustomer || patientData.customerRef)) || latestEp.customer || "" };
      if (isCpapFollowupNodeCustomerScopedUser(this.context && this.context.currentUser) && !this.filterAccessibleRecords([patientScope]).length) {
        return result;
      }
      const selectedNode = this.getSelectedNode(nodeName);
      const nodeId =
        (selectedNode && selectedNode.id) ||
        (nodeName === latestEp.node ? latestEp.nodeId || "" : "");

      const [latestTherapyStatus, sleepStudyReports] = await Promise.all([
        this.loadLatestTherapyStatus(hn, ""),
        this.loadPatientSleepStudyReports(hn),
      ]);

      return {
        patientData,
        latestEp,
        nodeName,
        nodeId,
        latestTherapyStatus,
        sleepStudyReports,
      };
    } catch (error) {
      console.error("Load patient info for import error:", error);
      return result;
    }
  };

  handleImportFollowupXlsxClick = () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    if (this.importFollowupXlsxRef.current) {
      this.importFollowupXlsxRef.current.value = "";
      this.importFollowupXlsxRef.current.click();
    }
  };

  getImportOrderFromRow = (row) => {
    return this.getExcelValue(row, [
      "Sleep Study Order",
      "sleep study order",
      "SleepStudyOrder",
    ]);
  };

  normalizeImportPatientKey = (hn = "") =>
    String(hn || "").trim().toUpperCase();

  formatImportExistingEventDate = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const dateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
      if (dateMatch) return dateMatch[0];
    }

    let dateValue = null;

    if (value.toDate) {
      dateValue = value.toDate();
    } else if (value.seconds !== undefined) {
      dateValue = new Date(value.seconds * 1000);
    } else if (value instanceof Date) {
      dateValue = value;
    } else {
      dateValue = new Date(value);
    }

    return dateValue && !isNaN(dateValue.getTime())
      ? formatLocalDate(dateValue)
      : "";
  };

  loadExistingFollowupImportIndex = async () => {
    const result = {
      orderSet: new Set(),
      patientEventMap: new Map(),
    };

    try {
      const snap = await getDocs(collection(db, COLLECTION_NAME));

      snap.forEach((item) => {
        const data = item.data();
        const ep = data.extendedProps || {};
        const order = String(ep.order || "").trim();
        const hn = String(ep.hn || "").trim();
        const patientKey = this.normalizeImportPatientKey(hn);

        if (order) {
          result.orderSet.add(order);
        }

        if (!this.filterAccessibleRecords([data]).length) return;
        if (patientKey) {
          const existingEvents = result.patientEventMap.get(patientKey) || [];

          existingEvents.push({
            hn,
            name: `${ep.firstname || ""} ${ep.lastname || ""}`.trim(),
            type: ep.type || "",
            date: this.formatImportExistingEventDate(
              ep.followupDate || ep.date || data.start,
            ),
            order,
          });

          result.patientEventMap.set(patientKey, existingEvents);
        }
      });
    } catch (error) {
      console.error("Load existing follow-up import index error:", error);
      throw error;
    }

    return result;
  };

  formatDuplicatePatientEventLogs = (hn, existingPatientEvents = []) =>
    existingPatientEvents.map((item) => {
      const patientName = item.name ? ` - ${item.name}` : "";

      return `${hn}${patientName} | Type: ${item.type || "-"} | Date: ${
        item.date || "-"
      }`;
    });

  exportImportProblemRows = (
    problemRows,
    fileName = "cpap-followup-import-problems.xlsx",
  ) => {
    if (!problemRows || problemRows.length === 0) return;

    const orderedRows = problemRows.map((row) => {
      const { ExcelRow, ImportError, ...rest } = row;

      return {
        ExcelRow: ExcelRow || "",
        ImportError: ImportError || "",
        ...rest,
      };
    });
    const detailHeaders = Array.from(
      new Set(
        orderedRows.flatMap((row) =>
          Object.keys(row).filter(
            (key) => key !== "ExcelRow" && key !== "ImportError",
          ),
        ),
      ),
    );

    const worksheet = XLSX.utils.json_to_sheet(orderedRows, {
      header: ["ExcelRow", "ImportError", ...detailHeaders],
    });

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Problems");
    XLSX.writeFile(workbook, fileName);
  };

  importFollowupEventsFromXlsx = async (event) => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    const file =
      event.target && event.target.files && event.target.files.length > 0
        ? event.target.files[0]
        : null;

    if (!file) return;

    try {
      this.setState({ isLoading: true });
      this.importCustomerNodeIndexPromise = null;

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, {
        type: "array",
        cellDates: true,
      });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
        range: 1,
      });

      const existingFollowupIndex =
        await this.loadExistingFollowupImportIndex();
      const existingOrderSet = existingFollowupIndex.orderSet;
      const existingPatientEventMap =
        existingFollowupIndex.patientEventMap;

      let createdCount = 0;
      const errorCounts = {
        duplicatePatient: 0,
        duplicateOrder: 0,
        missingSerial: 0,
        missingHn: 0,
        invalidReceiptDate: 0,
        other: 0,
      };

      const problemRows = [];

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const excelRowNumber = index + 2;

        const importedOrder = this.getImportOrderFromRow(row);
        const hasSleepStudyOrder = Boolean(importedOrder);
        const order = importedOrder || NO_SLEEP_STUDY_ORDER;

        const hn = this.getExcelValue(row, ["BDT's HN", "BDT HN", "HN"]);

        const serialNumber = this.getExcelValue(row, [
          "Serial Number",
          "SerialNumber",
          "Serial No.",
        ]);

        if (!hn) {
          errorCounts.missingHn += 1;
          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: "Missing BDT's HN",
          });
          continue;
        }

        const patientKey = this.normalizeImportPatientKey(hn);
        const existingPatientEvents =
          existingPatientEventMap.get(patientKey) || [];

        if (existingPatientEvents.length > 0) {
          errorCounts.duplicatePatient += 1;
          const existingEventTypes = existingPatientEvents
            .map((item) => item.type || "-")
            .join(" | ");
          const existingEventDates = existingPatientEvents
            .map((item) => item.date || "-")
            .join(" | ");
          const existingEventOrders = existingPatientEvents
            .map((item) => item.order || "-")
            .join(" | ");
          const existingPatientNames = Array.from(
            new Set(
              existingPatientEvents
                .map((item) => item.name)
                .filter((value) => value),
            ),
          ).join(" | ");
          const existingEventDescription = existingPatientEvents
            .map(
              (item) =>
                `Type: ${item.type || "-"}; Date: ${item.date || "-"}`,
            )
            .join(" | ");

          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: `Patient already has CPAP follow-up event: ${hn}; ${existingEventDescription}`,
            "Existing Patient HN": hn,
            "Existing Patient Name": existingPatientNames,
            "Existing Event Type": existingEventTypes,
            "Existing Event Date": existingEventDates,
            "Existing Event Order": existingEventOrders,
          });
          continue;
        }

        if (hasSleepStudyOrder && existingOrderSet.has(order)) {
          errorCounts.duplicateOrder += 1;
          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: `Sleep Study Order already exists: ${order}`,
          });
          continue;
        }

        if (!serialNumber) {
          errorCounts.missingSerial += 1;
          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: "Missing Serial Number",
          });
          continue;
        }

        const receiptDateRaw =
          row["Receipt Date"] || row["Receipt Date."] || row["receipt date"];

        const receiptDate = this.parseExcelDateToLocalDate(receiptDateRaw);

        if (!receiptDate) {
          errorCounts.invalidReceiptDate += 1;
          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: "Missing or invalid Receipt Date",
          });
          continue;
        }

        const followupDate = this.addDaysToDateString(receiptDate, 30);

        const ahi = this.getExcelValue(row, ["AHI", "ahi"]);
        const maskText = this.getExcelValue(row, ["Mask", "mask"]);
        const maskSize = this.getExcelValue(row, ["Size", "size"]);
        const importedCustomer = this.getImportCustomerFromRow(row);

        try {
          const [patientInfo, productInfo, importedCustomerLocation] =
            await Promise.all([
              this.loadPatientInfoForImport(hn),
              this.loadProductBySerialNumber(serialNumber),
              this.findImportCustomerLocation(importedCustomer),
            ]);

          const patientData = patientInfo.patientData || {};
          const latestEp = patientInfo.latestEp || {};
          const nodeName =
            importedCustomerLocation.nodeName || patientInfo.nodeName || "";
          const nodeId =
            importedCustomerLocation.nodeId || patientInfo.nodeId || "";
          const customer =
            importedCustomerLocation.customer ||
            patientData.refCustomer ||
            patientData.customerRef ||
            latestEp.customer ||
            "";
          const statusConfig = getStatusConfig("CPAP Follow Up");

          const uuid = doc(collection(db, COLLECTION_NAME)).id;
          const startDate = new Date(`${followupDate}T00:00:00`);

          const extendedProps = {
            ...emptyForm,

            uuid,
            date: followupDate,
            followupDate,

            type: "CPAP Follow-Up 1M",
            status: "CPAP Follow Up",
            doctorActionStatus: "No Doctor Action",

            hn,
            order,

            firstname:
              patientData.firstnameTh ||
              patientData.firstname ||
              latestEp.firstname ||
              "",

            lastname:
              patientData.lastnameTh ||
              patientData.lastname ||
              latestEp.lastname ||
              "",

            phone:
              patientData.mobile ||
              latestEp.phone ||
              this.getExcelValue(row, ["Tel", "Phone", "Mobile"]),

            hospitalRef:
              patientData.refHn ||
              latestEp.hospitalRef ||
              latestEp.refHospital ||
              latestEp.hospital ||
              this.getExcelValue(row, [
                "Hospital HN",
                "Ref HN",
                "รหัสโรงพยาบาล",
              ]),

            node: nodeName,
            nodeId,
            customer,

            underlyingDisease: patientData.comorbidity || "",

            prePurchaseAhi: ahi,
            averageAhi: ahi,

            serialNumber,
            brand: productInfo.brand || "",
            model: productInfo.model || "",
            cpapType: productInfo.cpapType || "Auto-CPAP",

            maskType: this.getMaskTypeFromMaskText(maskText),
            maskSize: maskSize || "กรุณาเลือก",

            latestTherapyStatus: null,
            latestTherapyStatusRecord: patientInfo.latestTherapyStatus
              ? {
                  source: "latest_previous_followup",
                  uuid: patientInfo.latestTherapyStatus.uuid || "",
                  date: patientInfo.latestTherapyStatus.date || "",
                  type: patientInfo.latestTherapyStatus.type || "",
                  status: patientInfo.latestTherapyStatus.status || "",
                  minPressure: patientInfo.latestTherapyStatus.minPressure || "",
                  maxPressure: patientInfo.latestTherapyStatus.maxPressure || "",
                  currentRampTime:
                    patientInfo.latestTherapyStatus.currentRampTime || "",
                  doctorActionStatus:
                    patientInfo.latestTherapyStatus.doctorActionStatus || "",
                  note: patientInfo.latestTherapyStatus.note || "",
                  recordedAt: new Date().toISOString(),
                }
              : null,

            usageReports: [],
            questionnaires: [],
            sleepStudyReports: Array.isArray(patientInfo.sleepStudyReports)
              ? patientInfo.sleepStudyReports
              : [],
          };

          const payload = {
            id: uuid,
            title: this.buildTitle(extendedProps),
            start: Timestamp.fromDate(startDate),
            allDay: true,
            color: statusConfig.color,
            extendedProps,
            createdBy: this.getCurrentUserName(),
            createdAt: serverTimestamp(),
            updatedBy: this.getCurrentUserName(),
            updatedAt: serverTimestamp(),
          };

          if (!this.filterAccessibleRecords([payload]).length) throw new Error("Follow-up is outside your assigned node/customer access");
          await setDoc(doc(db, COLLECTION_NAME, uuid), payload);

          if (hasSleepStudyOrder) {
            existingOrderSet.add(order);
          }
          existingPatientEventMap.set(patientKey, [
            {
              hn,
              name: `${extendedProps.firstname || ""} ${
                extendedProps.lastname || ""
              }`.trim(),
              type: extendedProps.type,
              date: followupDate,
              order,
            },
          ]);
          createdCount += 1;
        } catch (rowError) {
          console.error("Import CPAP follow-up row error:", rowError);
          errorCounts.other += 1;

          problemRows.push({
            ...row,
            ExcelRow: excelRowNumber,
            ImportError: rowError.message || "Import row error",
          });
        }
      }

      if (problemRows.length > 0) {
        this.exportImportProblemRows(
          problemRows,
          `cpap-followup-import-problems-${formatLocalDate(new Date())}.xlsx`,
        );
      }

      if (problemRows.length > 0) {
        const errorGroupSummary = [
          ["Duplicate patient", errorCounts.duplicatePatient],
          ["Duplicate Sleep Study Order", errorCounts.duplicateOrder],
          ["Missing serial number", errorCounts.missingSerial],
          ["Missing HN", errorCounts.missingHn],
          ["Invalid receipt date", errorCounts.invalidReceiptDate],
          ["Other import error", errorCounts.other],
        ]
          .filter(([, count]) => count > 0)
          .map(([label, count]) => `${label}: ${count}`)
          .join(" | ");

        notifyWarning(
          `Import complete: ${formatCaseCount(
            createdCount,
          )} passed, ${formatCaseCount(
            problemRows.length,
          )} failed. ${errorGroupSummary}. Open the import error file to check the errors.`,
        );
      } else {
        notifyTopRight(
          `Import complete: ${formatCaseCount(
            createdCount,
          )} passed, 0 cases failed.`,
        );
      }

      await this.fetchData();
    } catch (error) {
      console.error(error);
      notifyError("Import XLSX error");
    } finally {
      this.setState({ isLoading: false });

      if (this.importFollowupXlsxRef.current) {
        this.importFollowupXlsxRef.current.value = "";
      }
    }
  };

  getCurrentUserName = () => {
    if (
      this.context &&
      this.context.currentUser &&
      this.context.currentUser.displayName
    ) {
      return this.context.currentUser.displayName;
    }

    return "";
  };

  getCurrentRole = () =>
    getCpapFollowupRole(this.context && this.context.currentUser);

  isStaffReadOnly = () =>
    isStaffCpapFollowupViewer(
      this.context && this.context.currentUser,
    );

  getFileCount = (ep) => {
    const usageReports = Array.isArray(ep.usageReports) ? ep.usageReports : [];
    const questionnaires = Array.isArray(ep.questionnaires)
      ? ep.questionnaires
      : [];

    return usageReports.length + questionnaires.length;
  };

  getDoctorActionLabel = (value) => {
    const doctorActionOption = DOCTOR_ACTION_OPTIONS.find(
      (item) => item.value === value,
    );

    return doctorActionOption ? doctorActionOption.label : value || "-";
  };

  formatAuditDateTime = (value) => {
    if (!value) return "-";

    let dateValue = null;

    if (value.toDate) {
      dateValue = value.toDate();
    } else if (value.seconds) {
      dateValue = new Date(value.seconds * 1000);
    } else if (value instanceof Date) {
      dateValue = value;
    } else {
      dateValue = new Date(value);
    }

    if (!dateValue || isNaN(dateValue.getTime())) return "-";

    return dateValue.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  getFileExtension = (fileData) => {
    let fileName = "";

    if (fileData && fileData.fileName) {
      fileName = fileData.fileName;
    } else if (fileData && fileData.storagePath) {
      fileName = fileData.storagePath;
    }

    const cleanName = String(fileName).split("?")[0];
    const parts = cleanName.split(".");

    if (parts.length <= 1) return "";

    return parts[parts.length - 1].toLowerCase();
  };

  getFileUrl = async (fileData) => {
    if (!fileData) return "";

    if (fileData.url) {
      return fileData.url;
    }

    if (fileData.storagePath) {
      return await getDownloadURL(ref(storage, fileData.storagePath));
    }

    return "";
  };

  buildTitle = (extendedProps = {}) => {
    const hn = safeText(extendedProps.hn);
    const name = `${safeText(extendedProps.firstname)} ${safeText(
      extendedProps.lastname,
    )}`.trim();
    const type = safeText(extendedProps.type);

    if (hn && name) return `${hn} - ${name}`;
    if (hn) return `${hn} - ${type}`;
    return type || "CPAP Follow-Up";
  };

  fetchData = async () => {
    this.importCustomerNodeIndexPromise = null;
    const currentUser = this.context && this.context.currentUser;
    const accessKey = getCpapFollowupAccessKey(currentUser);
    this._followupAccessKey = accessKey;
    const requestId = this._followupRequestId = (this._followupRequestId || 0) + 1;
    try {
      this.setState({ isLoading: true, calendarEvents: [], filteredEvents: [], filterCustomerList: [], nodeList: [], staffAdminProfile: null, customerList: [] });

      const [followupSnap, nodeSnap, techSnap, doctorSnap] = await Promise.all([
        getDocs(collection(db, COLLECTION_NAME)),
        getDocs(collection(db, "nodes")),
        getDocs(collection(db, "sleeptechnicians")),
        getDocs(collection(db, "doctors")),
      ]);

      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      let nodes = nodeSnap.docs.map((nodeDoc) => ({
        id: nodeDoc.id,
        ...nodeDoc.data(),
      }));

      const currentRole = this.getCurrentRole();
      const currentUserName = this.getCurrentUserName();
      const scope = await loadCpapFollowupScope(currentUser, nodes);
      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      nodes = scope.nodes;
      const staffAdminProfile = scope.adminData;

      const allowedNodeNames = nodes.map((item) => item.nodename);

      let events = followupSnap.docs.map((item) => {
        const data = item.data();
        const ep = data.extendedProps || {};
        const date = toDateString(data.start || ep.date || ep.followupDate);

        return {
          ...data,
          id: data.id || item.id,
          title: data.title || this.buildTitle(ep),
          start: date,
          color: data.color || getStatusConfig(ep.status).color,
          extendedProps: {
            ...ep,
            uuid: ep.uuid || item.id,
            date,
            sideEffects: Array.isArray(ep.sideEffects) ? ep.sideEffects : [],
            usageReports: Array.isArray(ep.usageReports) ? ep.usageReports : [],
            questionnaires: Array.isArray(ep.questionnaires)
              ? ep.questionnaires
              : [],
            sleepStudyReports: Array.isArray(ep.sleepStudyReports)
              ? ep.sleepStudyReports
              : [],

            _createdBy: data.createdBy || "",
            _createdAt: data.createdAt || null,
            _updatedBy: data.updatedBy || "",
            _updatedAt: data.updatedAt || null,
          },
        };
      });

      if (currentRole === "sleep_physician") {
        events = events.filter(
          (event) => event.extendedProps.doctorConsult === currentUserName,
        );
      } else if (currentRole === "sale") {
        events = events.filter((event) =>
          allowedNodeNames.includes(event.extendedProps.node),
        );
      }

      events = filterCpapFollowupRecords({ records: events, currentUser, adminData: staffAdminProfile, nodes });

      const sleepStudyReportsMap =
        await this.loadAllPatientSleepStudyReportsMap(events);

      events = events.map((event) => {
        const ep = event.extendedProps || {};
        const hn = safeText(ep.hn).trim();

        return {
          ...event,
          extendedProps: {
            ...ep,
            sleepStudyReports: sleepStudyReportsMap[hn] || [],
          },
        };
      });

      const technicianList = [
        "กรุณาเลือก",
        ...techSnap.docs
          .filter((item) => item.data().status !== "Unavailable")
          .map((item) => item.data().name || item.data().nickname || item.id)
          .filter(Boolean),
      ];

      const doctorList =
        currentRole === "sleep_physician"
          ? ["กรุณาเลือก", currentUserName].filter(Boolean)
          : [
              "กรุณาเลือก",
              ...doctorSnap.docs
                .map((item) => item.data().name || item.id)
                .filter(Boolean),
            ];

      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      this.setState(
        {
          calendarEvents: events,
          filteredEvents: events,
          nodeList: nodes,
          staffAdminProfile,
          technicianList,
          doctorList,
          filters:
            currentRole === "sleep_physician"
              ? {
                  ...this.state.filters,
                  node: "All Node",
                  doctor: currentUserName,
                }
              : this.state.filters,
          isLoading: false,
        },
        this.applyFilters,
      );
    } catch (error) {
      if (requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;
      console.error(error);
      notifyError("Cannot load CPAP follow-up data");
      this.setState({ isLoading: false });
    }
  };

  buildLatestTherapyStatusRecord = () => {
    const { form } = this.state;

    const original = form.originalTherapyStatus;
    const latest = form.latestTherapyStatus;
    const savedRecord = form.latestTherapyStatusRecord;

    const isExistingEvent = Boolean(form.uuid);

    if (
      savedRecord &&
      savedRecord.source === "doctor_decision_before_reschedule"
    ) {
      return savedRecord;
    }

    const hasOriginal =
      original &&
      (original.type ||
        original.status ||
        original.doctorActionStatus ||
        original.note);

    const hasChanged =
      hasOriginal &&
      (original.type !== form.type ||
        original.status !== form.status ||
        original.doctorActionStatus !== form.doctorActionStatus ||
        original.note !== form.note);

    if (isExistingEvent && !hasChanged) {
      return savedRecord || null;
    }

    if (isExistingEvent && hasChanged) {
      return {
        source: "before_edit",
        date: original.date || "",
        type: original.type || "",
        status: original.status || "",
        minPressure: original.minPressure || "",
        maxPressure: original.maxPressure || "",
        currentRampTime: original.currentRampTime || "",
        doctorActionStatus: original.doctorActionStatus || "",
        note: original.note || "",
        recordedAt: new Date().toISOString(),
      };
    }

    if (!isExistingEvent && latest) {
      return {
        source: "latest_previous_followup",
        uuid: latest.uuid || "",
        date: latest.date || "",
        type: latest.type || "",
        status: latest.status || "",
        minPressure: latest.minPressure || "",
        maxPressure: latest.maxPressure || "",
        currentRampTime: latest.currentRampTime || "",
        doctorActionStatus: latest.doctorActionStatus || "",
        note: latest.note || "",
        recordedAt: new Date().toISOString(),
      };
    }

    return savedRecord || null;
  };
  loadPatientCpapFiles = async (hn) => {
    if (!hn) {
      return {
        usageReports: [],
        questionnaires: [],
      };
    }

    try {
      const filesRef = collection(db, "patients", hn, "cpapFollowupFiles");

      let fileSnap;

      try {
        fileSnap = await getDocs(
          query(filesRef, orderBy("uploadedAt", "desc")),
        );
      } catch (error) {
        fileSnap = await getDocs(filesRef);
      }

      const usageReports = [];
      const questionnaires = [];

      fileSnap.forEach((fileDoc) => {
        const data = fileDoc.data();

        const fileData = {
          id: fileDoc.id,
          fileName: data.fileName || "",
          savedFileName: data.savedFileName || "",
          storagePath: data.storagePath || "",
          url: data.url || "",
          uploadedAt: data.uploadedAt || "",
          uploadedBy: data.uploadedBy || "",
          hn: data.hn || hn,
          followupUuid: data.followupUuid || "",
          fileType: data.fileType || "",
        };

        if (data.fileType === "questionnaires") {
          questionnaires.push(fileData);
        } else {
          usageReports.push(fileData);
        }
      });

      return {
        usageReports,
        questionnaires,
      };
    } catch (error) {
      console.error("Load patient CPAP files error:", error);

      return {
        usageReports: [],
        questionnaires: [],
      };
    }
  };

  loadPatientSleepStudyReports = async (hn) => {
    if (!hn) return [];

    try {
      const reportRef = collection(db, "patients", hn, "report");
      const reportSnap = await getDocs(reportRef);

      return normalizeSleepStudyReportDocs(reportSnap.docs, hn);
    } catch (error) {
      console.error("Load patient sleep study reports error:", error);
      return [];
    }
  };

  loadAllPatientSleepStudyReportsMap = async (events = []) => {
    const hnList = [
      ...new Set(
        events
          .map((event) => {
            const ep = event && event.extendedProps ? event.extendedProps : {};
            return safeText(ep.hn).trim();
          })
          .filter(Boolean),
      ),
    ];
    const reportMap = {};

    await Promise.all(
      hnList.map(async (hn) => {
        reportMap[hn] = await this.loadPatientSleepStudyReports(hn);
      }),
    );

    return reportMap;
  };

  loadLatestTherapyStatus = async (hn, currentUuid = "") => {
    if (!hn) return null;

    try {
      const followupQ = query(
        collection(db, COLLECTION_NAME),
        where("extendedProps.hn", "==", hn),
      );

      const snap = await getDocs(followupQ);
      const list = [];

      snap.forEach((item) => {
        const data = item.data();
        if (!this.filterAccessibleRecords([data]).length) return;
        const ep = data.extendedProps || {};
        const uuid = ep.uuid || data.id || item.id;

        if (currentUuid && uuid === currentUuid) return;

        list.push({
          uuid,
          date: ep.date || ep.followupDate || toDateString(data.start),
          type: ep.type || "",
          status: ep.status || "",
          minPressure: ep.minPressure || "",
          maxPressure: ep.maxPressure || "",
          currentRampTime: ep.currentRampTime || "",
          doctorActionStatus: ep.doctorActionStatus || "",
          note: ep.note || "",
        });
      });

      list.sort((a, b) => {
        return String(b.date || "").localeCompare(String(a.date || ""));
      });

      return list.length > 0 ? list[0] : null;
    } catch (error) {
      console.error("Load latest therapy status error:", error);
      return null;
    }
  };

  getSelectedNode = (nodeName) => {
    return (
      this.state.nodeList.find((item) => item.nodename === nodeName) || null
    );
  };

  fetchCustomersByNodeName = async (
    nodeName,
    stateKey = "customerList",
  ) => {
    const selectedNode = this.getSelectedNode(nodeName);

    if (!selectedNode) {
      this.setState({ [stateKey]: [] });
      return [];
    }

    try {
      const customerSnap = await getDocs(
        collection(db, "nodes", selectedNode.id, "customers"),
      );

      const loadedCustomers = customerSnap.docs.map((item) => ({
        id: item.id,
        ...item.data(),
        displayName:
          item.data().CustomerNameEN || item.data().customerName || item.id,
      }));
      const customerList = filterCpapFollowupCustomers({
        customers: loadedCustomers,
        currentUser: this.context && this.context.currentUser,
        adminData: this.state.staffAdminProfile,
        node: selectedNode,
      });

      if (
        stateKey !== "filterCustomerList" ||
        this.state.filters.node === nodeName
      ) {
        this.setState({ [stateKey]: customerList });
      }
      return customerList;
    } catch (error) {
      console.error(error);
      if (
        stateKey !== "filterCustomerList" ||
        this.state.filters.node === nodeName
      ) {
        this.setState({ [stateKey]: [] });
      }
      return [];
    }
  };

  handleFilterChange = (name, value) => {
    if (name === "doctor" && this.isStaffReadOnly()) {
      return;
    }

    const isSuperAdmin = this.getCurrentRole() === "super_admin";

    if (name === "customer" && !isSuperAdmin) {
      return;
    }

    const isNodeFilter = name === "node";

    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          [name]: value,
          ...(isNodeFilter ? { customer: "All Customer" } : {}),
        },
        ...(isNodeFilter && isSuperAdmin ? { filterCustomerList: [] } : {}),
      }),
      async () => {
        // Event filtering uses already-loaded data; do not wait for the
        // separate customer options request to update the visible calendar.
        this.applyFilters();

        if (isNodeFilter && isSuperAdmin && value !== "All Node") {
          await this.fetchCustomersByNodeName(value, "filterCustomerList");
        }
      },
    );
  };

  normalizeFollowupSearchValue = (value) =>
    String(value || "")
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[‐‑‒–—―]/g, "-")
      .trim();

  compactFollowupSearchValue = (value) =>
    this.normalizeFollowupSearchValue(value).replace(/[^a-z0-9ก-๙]+/g, "");

  getFilteredCalendarEvents = (
    events = this.state.calendarEvents,
    filters = this.state.filters,
    searchText = this.state.followupSearchText,
  ) => {
    const normalizedSearch = this.normalizeFollowupSearchValue(searchText);
    const compactSearch = this.compactFollowupSearchValue(searchText);
    const isStaffReadOnly = this.isStaffReadOnly();
    const isSuperAdmin = this.getCurrentRole() === "super_admin";
    const customerFilter = filters.customer || "All Customer";

    return events.filter((event) => {
      const ep = event.extendedProps || {};

      if (filters.node !== "All Node" && ep.node !== filters.node) return false;

      if (
        isSuperAdmin &&
        customerFilter !== "All Customer" &&
        ep.customer !== customerFilter
      ) {
        return false;
      }

      if (filters.status !== "All Status" && ep.status !== filters.status) {
        return false;
      }

      if (filters.type !== "All Type" && ep.type !== filters.type) {
        return false;
      }

      // if (
      //   filters.technician !== "All Technician" &&
      //   ep.technicianConsult !== filters.technician
      // ) {
      //   return false;
      // }

      if (filters.averageAhi !== "All Average AHI") {
        const averageAhi = parseAverageAhi(ep.averageAhi);

        if (filters.averageAhi === "EMPTY_OR_ZERO") {
          if (!(averageAhi === null || averageAhi === 0)) {
            return false;
          }
        }

        if (filters.averageAhi === "LESS_THAN_30") {
          if (!(averageAhi !== null && averageAhi > 0 && averageAhi < 30)) {
            return false;
          }
        }

        if (filters.averageAhi === "BETWEEN_30_60") {
          if (!(averageAhi !== null && averageAhi >= 30 && averageAhi <= 60)) {
            return false;
          }
        }

        if (filters.averageAhi === "MORE_THAN_60") {
          if (!(averageAhi !== null && averageAhi > 60)) {
            return false;
          }
        }
      }

      if (
        !isStaffReadOnly &&
        filters.doctor !== "All Doctor" &&
        ep.doctorConsult !== filters.doctor
      ) {
        return false;
      }

      if (
        filters.doctorActionStatus !== "All Doctor Action" &&
        ep.doctorActionStatus !== filters.doctorActionStatus
      ) {
        return false;
      }

      if (normalizedSearch) {
        const searchableText = [
          ep.order || event.order,
          event.title,
          ep.hn,
          ep.firstname,
          ep.lastname,
          `${ep.firstname || ""} ${ep.lastname || ""}`,
        ]
          .filter(Boolean)
          .join(" ");
        const normalizedSearchableText =
          this.normalizeFollowupSearchValue(searchableText);
        const compactSearchableText =
          this.compactFollowupSearchValue(searchableText);

        return (
          normalizedSearchableText.includes(normalizedSearch) ||
          (compactSearch && compactSearchableText.includes(compactSearch))
        );
      }

      return true;
    });
  };

  applyFilters = () => {
    const filteredEvents = this.getFilteredCalendarEvents();

    this.setState({ filteredEvents });

    return filteredEvents;
  };

  focusCalendarOnSearchResult = (events) => {
    if (
      !events ||
      events.length !== 1 ||
      !this.calendarComponentRef.current ||
      !events[0].start
    ) {
      return;
    }

    this.calendarComponentRef.current.getApi().gotoDate(events[0].start);
  };

  onSearchFollowup = (event) => {
    const followupSearchText = event.target.value;

    if (!followupSearchText.trim()) {
      this.onClearFollowupSearch();
      return;
    }

    this.setState(
      { followupSearchText },
      () => {
        const filteredEvents = this.applyFilters();
        this.focusCalendarOnSearchResult(filteredEvents);
      },
    );
  };

  onClearFollowupSearch = () => {
    // Keep the displayed month so clearing a successful search does not
    // navigate away from the case the operator has just found.
    this.setState({ followupSearchText: "" }, this.applyFilters);
  };

  openAddModal = async (dateStr = null) => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    this.pendingCpapUsageReports = [];

    const nextForm = {
      ...emptyForm,
      date: dateStr || formatLocalDate(new Date()),
      sideEffects: [],
      usageReports: [],
      questionnaires: [],
      sleepStudyReports: [],
    };

    this.setState({
      form: nextForm,
      selectedEvent: null,
      activeTab: "patient",
      isAddEventModal: true,
      isEditEventModal: false,
      isEditMode: true,
      isNewPatient: false,
      customerList: [],
    });
  };

  openEditModal = async (clickInfo) => {
    this.pendingCpapUsageReports = [];

    const event = clickInfo.event;
    const ep = event.extendedProps || {};

    const form = {
      ...emptyForm,
      ...ep,
      uuid: ep.uuid || event.id,
      date: toDateString(event.start || ep.date),
      sideEffects: Array.isArray(ep.sideEffects) ? ep.sideEffects : [],
      usageReports: Array.isArray(ep.usageReports) ? ep.usageReports : [],
      questionnaires: Array.isArray(ep.questionnaires) ? ep.questionnaires : [],
      sleepStudyReports: Array.isArray(ep.sleepStudyReports)
        ? ep.sleepStudyReports
        : [],
      latestTherapyStatus: null,
      latestTherapyStatusRecord: ep.latestTherapyStatusRecord || null,

      originalTherapyStatus: {
        date: toDateString(event.start || ep.date),
        type: ep.type || "",
        status: ep.status || "",
        minPressure: ep.minPressure || "",
        maxPressure: ep.maxPressure || "",
        currentRampTime: ep.currentRampTime || "",
        doctorActionStatus: ep.doctorActionStatus || "",
        note: ep.note || "",
      },
    };

    if (form.node) {
      await this.fetchCustomersByNodeName(form.node);
    }

    if (form.hn) {
      const [patientFiles, sleepStudyReports, latestTherapyStatus] =
        await Promise.all([
          this.loadPatientCpapFiles(form.hn),
          this.loadPatientSleepStudyReports(form.hn),
          this.loadLatestTherapyStatus(form.hn, form.uuid),
        ]);

      form.usageReports = patientFiles.usageReports;
      form.questionnaires = patientFiles.questionnaires;
      form.sleepStudyReports = sleepStudyReports;
      form.latestTherapyStatus = latestTherapyStatus;
    }

    this.setState({
      form,
      selectedEvent: event,
      activeTab: "patient",
      isAddEventModal: false,
      isEditEventModal: true,
      isEditMode: false,
      isNewPatient: false,
    });
  };
  closeModal = () => {
    this.pendingCpapUsageReports = [];

    this.setState({
      isAddEventModal: false,
      isEditEventModal: false,
      isEditMode: true,
      isNewPatient: false,
      selectedEvent: null,
      form: { ...emptyForm },
      activeTab: "patient",
    });
  };

  addDaysToDateString = (dateString, days) => {
    const baseDate = dateString
      ? new Date(`${dateString}T00:00:00`)
      : new Date();

    if (isNaN(baseDate.getTime())) {
      return formatLocalDate(new Date());
    }

    baseDate.setDate(baseDate.getDate() + days);

    return formatLocalDate(baseDate);
  };

  getNextFollowupTypeForContinueTherapy = (currentType, nodeName) =>
    getNextFollowupTypeForNode(currentType, nodeName, this.state.nodeList);

  buildDoctorDecisionRecord = (form, doctorActionValue) => {
    return {
      source: "doctor_decision_before_reschedule",
      date: form.date || "",
      type: form.type || "",
      status: form.status || "",
      minPressure: form.minPressure || "",
      maxPressure: form.maxPressure || "",
      currentRampTime: form.currentRampTime || "",
      doctorActionStatus: doctorActionValue || "",
      note: form.note || "",
      recordedAt: new Date().toISOString(),
    };
  };

  handleDoctorActionChange = (value) => {
    this.setState((prevState) => {
      const form = prevState.form;

      const nextForm = {
        ...form,
        doctorActionStatus: value,
      };

      if (value === "Continue Therapy") {
        nextForm.latestTherapyStatusRecord = this.buildDoctorDecisionRecord(
          form,
          value,
        );

        nextForm.type = this.getNextFollowupTypeForContinueTherapy(
          form.type,
          form.node,
        );
        nextForm.status = "CPAP Follow Up";

        if (form.type === "CPAP Follow-Up 1Y") {
          nextForm.date = this.addDaysToDateString(form.date, 365);
        } else {
          nextForm.date = this.addDaysToDateString(form.date, 90);
        }

        // Reset current doctor review for the next follow-up cycle
        nextForm.doctorActionStatus = "No Doctor Action";
      }

      if (value === "Adjust Pressure") {
        nextForm.latestTherapyStatusRecord = this.buildDoctorDecisionRecord(
          form,
          value,
        );

        nextForm.type = form.type;
        nextForm.status = "Technical Solution";
        nextForm.date = this.addDaysToDateString(form.date, 14);

        // Keep doctor action because the pressure-adjustment loop is still active
        nextForm.doctorActionStatus = "Adjust Pressure";
      }

      return {
        form: nextForm,
      };
    });
  };

  getNextFollowupTypeForContinueTherapy = (currentType, nodeName) =>
    getNextFollowupTypeForNode(currentType, nodeName, this.state.nodeList);

  handleDoctorActionChange = (value) => {
    this.setState((prevState) => {
      const form = prevState.form;
      const nextForm = {
        ...form,
        doctorActionStatus: value,
      };

      if (value === "Continue Therapy") {
        nextForm.type = this.getNextFollowupTypeForContinueTherapy(
          form.type,
          form.node,
        );
        nextForm.status = "CPAP Follow Up";

        if (form.type === "CPAP Follow-Up 1Y") {
          nextForm.date = this.addDaysToDateString(form.date, 365);
        } else {
          nextForm.date = this.addDaysToDateString(form.date, 180);
        }
      }

      if (value === "Adjust Pressure") {
        nextForm.type = form.type;
        nextForm.status = "Technical Solution";
        nextForm.date = this.addDaysToDateString(form.date, 14);
      }

      return {
        form: nextForm,
      };
    });
  };

  handleInputChange = (name, value) => {
    this.setState((prevState) => {
      const nextForm = {
        ...prevState.form,
        [name]: value,
      };

      if (name === "cpapReportType") {
        nextForm.leakRate = getDefaultLeakRateForCpapReportType(value);
      }

      return {
        form: nextForm,
      };
    });
  };

  handleNodeChange = async (nodeName) => {
    const selectedNode = this.getSelectedNode(nodeName);

    this.setState((prevState) => {
      const typeOptions = getFollowupTypeOptionsForNode(
        nodeName,
        prevState.nodeList,
      );

      return {
        form: {
          ...prevState.form,
          node: nodeName,
          nodeId: selectedNode ? selectedNode.id : "",
          customer: "",
          type: typeOptions.includes(prevState.form.type)
            ? prevState.form.type
            : typeOptions[0],
        },
      };
    });

    await this.fetchCustomersByNodeName(nodeName);
  };

  handleSideEffectChange = (option, checked) => {
    this.setState((prevState) => {
      const sideEffects = updateCpapSideEffectSelection(
        prevState.form.sideEffects,
        option,
        checked,
      );

      return {
        form: {
          ...prevState.form,
          sideEffects,
        },
      };
    });
  };

  getLatestTimestampSeconds = (item) => {
    if (item && item.createdAt && item.createdAt.seconds) {
      return item.createdAt.seconds;
    }

    if (item && item.start && item.start.seconds) {
      return item.start.seconds;
    }

    return 0;
  };

  getMaskTypeFromProductName = (productName = "") => {
    const text = String(productName || "").toLowerCase();

    if (text.includes("airtouch") && text.includes("f20")) {
      return "Full Face Mask AirTouch F20";
    }
    if (text.includes("airfit") && text.includes("f20")) {
      return "Full Face Mask AirFit F20";
    }
    if (text.includes("airfit") && text.includes("n30i")) {
      return "Nasal Mask AirFit N30i";
    }
    if (text.includes("airfit") && text.includes("n30")) {
      return "Nasal Mask AirFit N30";
    }
    if (text.includes("airfit") && text.includes("n20")) {
      return "Nasal Mask AirFit N20";
    }
    if (text.includes("airfit") && text.includes("p30i")) {
      return "Nasal Pillow AirFit P30i";
    }
    if (text.includes("airfit") && text.includes("p10")) {
      return "Nasal Pillow AirFit P10";
    }
    if (text.includes("rescomf") && text.includes("full")) {
      return "Full Face Mask ResComf";
    }
    if (text.includes("rescomf")) {
      return "Nasal Mask ResComf";
    }
    if (text.includes("wizard 320")) {
      return "Full Face Mask Wizard 320";
    }
    if (text.includes("wizard 310")) {
      return "Nasal Mask Wizard 310";
    }
    if (text.includes("fm50")) {
      return "Topson Full Face FM50 Mask";
    }
    if (text.includes("nm40")) {
      return "Topson Nasal NM40 Mask";
    }
    if (text.includes("nm50")) {
      return "Topson Nasal NM50 Mask";
    }
    if (text.includes("wisp")) {
      return "Philips Wisp Nasal Mask";
    }
    if (text.includes("pico")) {
      return "Philips Pico Nasal Mask";
    }
    if (text.includes("amara")) {
      return "Philips Amara Gel Full Face Mask";
    }
    if (text.includes("nasal pillow")) return "Nasal Pillow";
    if (text.includes("full face")) return "Full Face Mask";
    if (text.includes("oronasal")) return "Oronasal Mask";
    if (text.includes("nasal mask") || text.includes("nasal")) {
      return "Nasal Mask";
    }

    return "Other";
  };

  normalizeCpapType = (type = "") => {
    const text = String(type || "")
      .trim()
      .toLowerCase();

    if (text === "apap" || text === "auto cpap" || text === "auto-cpap") {
      return "Auto-CPAP";
    }

    if (text === "bipap" || text === "bi-pap") {
      return "BiPAP";
    }

    if (text === "cpap") {
      return "CPAP";
    }

    return type || "Auto-CPAP";
  };

  loadSaleOrderCpapInfo = async (hn) => {
    const result = {
      serialNumber: "",
      brand: "",
      model: "",
      cpapType: "",
      maskType: "",
      maskSize: "",
    };

    if (!hn) return result;

    try {
      const saleOrderQ = query(
        collection(db, "saleOrders"),
        where("hn", "==", hn),
      );

      const saleOrderSnap = await getDocs(saleOrderQ);

      const saleOrderList = [];

      saleOrderSnap.forEach((item) => {
        const data = item.data();

        saleOrderList.push({
          id: item.id,
          ...data,
        });
      });

      // Try newest sale order first if createdAt exists
      saleOrderList.sort((a, b) => {
        const aTime = this.getLatestTimestampSeconds(a);
        const bTime = this.getLatestTimestampSeconds(b);
        return bTime - aTime;
      });

      let cpapProductSn = "";

      for (const saleOrder of saleOrderList) {
        const items = Array.isArray(saleOrder.items) ? saleOrder.items : [];

        for (const orderItem of items) {
          const productType = String(orderItem.productType || "").toLowerCase();

          if (productType === "mask") {
            if (!result.maskType) {
              result.maskType = this.getMaskTypeFromProductName(
                orderItem.productName,
              );
            }

            if (!result.maskSize) {
              result.maskSize = orderItem.size || "";
            }
          }

          if (productType === "cpap") {
            if (!cpapProductSn) {
              cpapProductSn =
                orderItem.productSn ||
                orderItem.serialNumber ||
                orderItem.productSerialNumber ||
                "";
            }
          }
        }
      }

      if (cpapProductSn) {
        result.serialNumber = cpapProductSn;

        const productQ = query(
          collection(db, "products"),
          where("serialNumber", "==", cpapProductSn),
        );

        const productSnap = await getDocs(productQ);

        let productData = null;

        productSnap.forEach((item) => {
          if (!productData) productData = item.data();
        });

        if (productData) {
          result.brand = productData.brand || "";
          result.model = productData.model || "";
          result.cpapType = this.normalizeCpapType(productData.type);
        }
      }

      return result;
    } catch (error) {
      console.error("Load sale order CPAP info error:", error);
      return result;
    }
  };

  onSearchPatient = async () => {
    const hn = this.state.form.hn.trim();

    if (!hn) {
      notifyWarning("Please fill HN first");
      return;
    }

    const lookupAccessKey = getCpapFollowupAccessKey(this.context && this.context.currentUser);
    try {
      const patientQ = query(collection(db, "patients"), where("hn", "==", hn));

      const bookingQ = query(
        collectionGroup(db, "bookings"),
        where("extendedProps.hn", "==", hn),
      );

      const [patientSnap, bookingSnap, saleOrderCpapInfo] = await Promise.all([
        getDocs(patientQ),
        getDocs(bookingQ),
        this.loadSaleOrderCpapInfo(hn),
      ]);

      if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      let patientData = null;

      patientSnap.forEach((item) => {
        if (!patientData) patientData = item.data();
      });

      const bookingList = [];
      bookingSnap.forEach((item) => {
        if (this.filterAccessibleRecords([item.data()]).length) bookingList.push(item.data());
      });

      bookingList.sort((a, b) => {
        const aCreated = this.getLatestTimestampSeconds(a);
        const bCreated = this.getLatestTimestampSeconds(b);
        return bCreated - aCreated;
      });

      const latestBooking = bookingList[0] || null;
      const latestEp =
        latestBooking && latestBooking.extendedProps
          ? latestBooking.extendedProps
          : {};

      const nodeName =
        (patientData && patientData.nodeRef) || latestEp.node || "";
      const patientScope = { node: nodeName, customer: (patientData && (patientData.refCustomer || patientData.customerRef)) || latestEp.customer || "" };
      if (isCpapFollowupNodeCustomerScopedUser(this.context && this.context.currentUser) && !this.filterAccessibleRecords([patientScope]).length) {
        this.setState({ form: { ...emptyForm, hn }, customerList: [] });
        notifyWarning("This patient is outside your assigned node/customer access");
        return;
      }
      const selectedNode = this.getSelectedNode(nodeName);
      const nodeId =
        (selectedNode && selectedNode.id) ||
        (nodeName === latestEp.node ? latestEp.nodeId || "" : "");

      if (!patientData) {
        if (nodeName) {
          await this.fetchCustomersByNodeName(nodeName);
        }

        if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

        this.setState((prevState) => ({
          isNewPatient: true,
          form: {
            ...prevState.form,
            node: nodeName,
            nodeId,
            customer: latestEp.customer || "",
          },
        }));
        notifyWarning("New patient or patient not found");
        return;
      }

      if (nodeName) {
        await this.fetchCustomersByNodeName(nodeName);
      }

      const [patientFiles, sleepStudyReports, latestTherapyStatus] =
        await Promise.all([
          this.loadPatientCpapFiles(hn),
          this.loadPatientSleepStudyReports(hn),
          this.loadLatestTherapyStatus(hn, this.state.form.uuid),
        ]);

      if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      this.setState((prevState) => ({
        isNewPatient: false,
        form: {
          ...prevState.form,

          order: latestEp.order || prevState.form.order,

          firstname:
            patientData.firstnameTh ||
            patientData.firstname ||
            prevState.form.firstname,

          lastname:
            patientData.lastnameTh ||
            patientData.lastname ||
            prevState.form.lastname,

          prePurchaseAhi:
            latestEp.ahi ||
            latestEp.AHI ||
            patientData.ahi ||
            patientData.AHI ||
            patientData.prePurchaseAhi ||
            prevState.form.prePurchaseAhi,

          phone: patientData.mobile || prevState.form.phone,

          underlyingDisease:
            patientData.comorbidity || prevState.form.underlyingDisease,

          hospitalRef:
            patientData.refHn ||
            latestEp.hospitalRef ||
            latestEp.refHospital ||
            latestEp.hospital ||
            prevState.form.hospitalRef,

          node: nodeName,
          nodeId,

          customer:
            patientData.refCustomer ||
            patientData.customerRef ||
            latestEp.customer ||
            "",

          // Files
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
          sleepStudyReports,
          latestTherapyStatus: latestTherapyStatus,

          // From saleOrders + products
          serialNumber:
            saleOrderCpapInfo.serialNumber || prevState.form.serialNumber,

          brand: saleOrderCpapInfo.brand || prevState.form.brand,

          model: saleOrderCpapInfo.model || prevState.form.model,

          cpapType: saleOrderCpapInfo.cpapType || prevState.form.cpapType,

          maskType: saleOrderCpapInfo.maskType || prevState.form.maskType,

          maskSize: saleOrderCpapInfo.maskSize || prevState.form.maskSize,
        },
      }));
    } catch (error) {
      console.error(error);
      notifyError("Search patient error");
    }
  };

  // onSearchPatient = async () => {
  //   const hn = this.state.form.hn.trim();

  //   if (!hn) {
  //     notifyWarning("Please fill HN first");
  //     return;
  //   }

  //   try {
  //     const patientQ = query(collection(db, "patients"), where("hn", "==", hn));

  //     const bookingQ = query(
  //       collectionGroup(db, "bookings"),
  //       where("extendedProps.hn", "==", hn),
  //     );

  //     const [patientSnap, bookingSnap] = await Promise.all([
  //       getDocs(patientQ),
  //       getDocs(bookingQ),
  //     ]);

  //     let patientData = null;

  //     patientSnap.forEach((item) => {
  //       if (!patientData) patientData = item.data();
  //     });

  //     const bookingList = [];
  //     bookingSnap.forEach((item) => bookingList.push(item.data()));

  //     bookingList.sort((a, b) => {
  //       const aCreated = this.getLatestTimestampSeconds(a);
  //       const bCreated = this.getLatestTimestampSeconds(b);
  //       return bCreated - aCreated;
  //     });

  //     const latestBooking = bookingList[0] || null;
  //     const latestEp =
  //       latestBooking && latestBooking.extendedProps
  //         ? latestBooking.extendedProps
  //         : {};

  //     if (!patientData) {
  //       this.setState({ isNewPatient: true });
  //       notifyWarning("New patient or patient not found");
  //       return;
  //     }

  //     const nodeName =
  //       latestEp.node || patientData.nodeRef || this.state.form.node;

  //     if (nodeName) {
  //       await this.fetchCustomersByNodeName(nodeName);
  //     }

  //     const patientFiles = await this.loadPatientCpapFiles(hn);
  //     const latestTherapyStatus = await this.loadLatestTherapyStatus(
  //       hn,
  //       this.state.form.uuid,
  //     );

  //     this.setState((prevState) => ({
  //       isNewPatient: false,
  //       form: {
  //         ...prevState.form,
  //         order: latestEp.order || prevState.form.order,
  //         firstname:
  //           patientData.firstnameTh ||
  //           patientData.firstname ||
  //           prevState.form.firstname,
  //         lastname:
  //           patientData.lastnameTh ||
  //           patientData.lastname ||
  //           prevState.form.lastname,
  //         prePurchaseAhi:
  //           latestEp.ahi ||
  //           latestEp.AHI ||
  //           patientData.ahi ||
  //           patientData.AHI ||
  //           patientData.prePurchaseAhi ||
  //           prevState.form.prePurchaseAhi,
  //         phone: patientData.mobile || prevState.form.phone,
  //         underlyingDisease:
  //           patientData.comorbidity || prevState.form.underlyingDisease,
  //         hospitalRef:
  //           patientData.refHn ||
  //           latestEp.hospitalRef ||
  //           latestEp.refHospital ||
  //           latestEp.hospital ||
  //           prevState.form.hospitalRef,
  //         node: nodeName || prevState.form.node,
  //         nodeId: latestEp.nodeId || prevState.form.nodeId,
  //         customer:
  //           latestEp.customer ||
  //           patientData.customerRef ||
  //           prevState.form.customer,
  //         usageReports: patientFiles.usageReports,
  //         questionnaires: patientFiles.questionnaires,
  //         latestTherapyStatus: latestTherapyStatus,
  //       },
  //     }));
  //   } catch (error) {
  //     console.error(error);
  //     notifyError("Search patient error");
  //   }
  // };

  validateForm = () => {
    const { form } = this.state;

    if (!form.hn.trim()) {
      notifyError("Please fill HN");
      return false;
    }

    if (!form.firstname.trim() && !form.lastname.trim()) {
      notifyError("Please fill patient name");
      return false;
    }

    if (!form.date) {
      notifyError("Please select date");
      return false;
    }

    if (
      !isFollowupTypeAllowedForNode(
        form.type,
        form.node,
        this.state.nodeList,
      )
    ) {
      notifyError("LPH node supports CPAP Follow-Up 1M, 6M, and 1Y only");
      return false;
    }

    return true;
  };

  buildEventPayload = (uuid) => {
    const { form } = this.state;
    const statusConfig = getStatusConfig(form.status);
    const date = form.date || formatLocalDate(new Date());
    const startDate = new Date(`${date}T00:00:00`);

    const latestTherapyStatusRecord = this.buildLatestTherapyStatusRecord();

    const {
      latestTherapyStatus,
      originalTherapyStatus,
      sleepStudyReports,
      _createdBy,
      _createdAt,
      _updatedBy,
      _updatedAt,
      ...saveForm
    } = form;

    const extendedProps = {
      ...saveForm,
      uuid,
      followupDate: date,
      date,
      latestTherapyStatusRecord: latestTherapyStatusRecord,
      sideEffects: Array.isArray(form.sideEffects) ? form.sideEffects : [],
      usageReports: Array.isArray(form.usageReports) ? form.usageReports : [],
      questionnaires: Array.isArray(form.questionnaires)
        ? form.questionnaires
        : [],
    };

    return {
      id: uuid,
      title: this.buildTitle(extendedProps),
      start: Timestamp.fromDate(startDate),
      allDay: true,
      color: statusConfig.color,
      extendedProps,
    };
  };

  addFollowupRecord = async (payload, action, userName) => {
    const ep = payload.extendedProps || {};
    const hn = (ep.hn || "").trim();
    const decisionRecord = ep.latestTherapyStatusRecord || {};
    const doctorActionStatus =
      action === "doctor_action"
        ? decisionRecord.doctorActionStatus || ep.doctorActionStatus || ""
        : ep.doctorActionStatus || "";

    if (!hn) return;

    const recordRef = doc(collection(db, "patients", hn, "followupRecord"));

    await setDoc(recordRef, {
      source: COLLECTION_NAME,
      action,
      followupUuid: ep.uuid || payload.id || "",
      title: payload.title || "",
      order: ep.order || "",
      hn,
      firstname: ep.firstname || "",
      lastname: ep.lastname || "",
      phone: ep.phone || "",
      node: ep.node || "",
      customer: ep.customer || "",
      type: ep.type || "",
      status: ep.status || "",
      doctorActionStatus,
      technicianConsult: ep.technicianConsult || "",
      doctorConsult: ep.doctorConsult || "",
      serialNumber: ep.serialNumber || "",
      brand: ep.brand || "",
      model: ep.model || "",
      cpapType: ep.cpapType || "",
      cpapReportType: ep.cpapReportType || "",
      usageMoreThan4HoursPercent: ep.usageMoreThan4HoursPercent || "",
      averageUsageHours: ep.averageUsageHours || "",
      averageAhi: ep.averageAhi || "",
      pressure95Percent: ep.pressure95Percent || "",
      averageLeak: ep.averageLeak || "",
      leakAssessment: ep.leakAssessment || "",
      essScore: ep.essScore || "",
      currentWeight: ep.currentWeight || "",
      weightChange: ep.weightChange || "",
      wakeFeeling: ep.wakeFeeling || "",
      daytimeSleepiness: ep.daytimeSleepiness || "",
      sideEffects: Array.isArray(ep.sideEffects) ? ep.sideEffects : [],
      sideEffectsOther: ep.sideEffectsOther || "",
      note: ep.note || "",
      start: payload.start || null,
      date: ep.date || ep.followupDate || "",
      usageReports: Array.isArray(ep.usageReports) ? ep.usageReports : [],
      questionnaires: Array.isArray(ep.questionnaires) ? ep.questionnaires : [],
      recordBy: userName || "",
      recordAt: serverTimestamp(),
      extendedProps: ep,
    });
  };

  getFollowupRecordAction = (payload, fallbackAction = "update") => {
    const ep = payload.extendedProps || {};
    const decisionRecord = ep.latestTherapyStatusRecord || {};

    if (
      decisionRecord.source === "doctor_decision_before_reschedule" ||
      decisionRecord.doctorActionStatus
    ) {
      return "doctor_action";
    }

    const original = this.state.form && this.state.form.originalTherapyStatus;

    if (
      original &&
      original.doctorActionStatus &&
      original.doctorActionStatus !== ep.doctorActionStatus
    ) {
      return "doctor_action";
    }

    return fallbackAction;
  };

  onSubmitEvent = async () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    if (!this.validateForm()) return;

    try {
      const currentUserName = this.getCurrentUserName();
      const isUpdate = Boolean(this.state.form.uuid);

      if (!isUpdate) {
        const existingFollowupIndex =
          await this.loadExistingFollowupImportIndex();
        const patientKey = this.normalizeImportPatientKey(
          this.state.form.hn,
        );
        const existingPatientEvents =
          existingFollowupIndex.patientEventMap.get(patientKey) || [];

        if (existingPatientEvents.length > 0) {
          notifyError(
            `Patient already has CPAP follow-up event:\n${this.formatDuplicatePatientEventLogs(
              this.state.form.hn,
              existingPatientEvents,
            ).join("\n")}\nNo new event was created.`,
          );
          return;
        }
      }

      const uuid =
        this.state.form.uuid || doc(collection(db, COLLECTION_NAME)).id;
      const payload = this.buildEventPayload(uuid);
      const refDoc = doc(db, COLLECTION_NAME, uuid);
      const pendingUsageReports = [...this.pendingCpapUsageReports];

      if (isUpdate) {
        await updateDoc(refDoc, {
          ...payload,
          updatedBy: currentUserName,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(refDoc, {
          ...payload,
          createdBy: currentUserName,
          createdAt: serverTimestamp(),
          updatedBy: currentUserName,
          updatedAt: serverTimestamp(),
        });

        // A new event has an id only after its first save. Keep it in local
        // form state so a failed pending-file upload can be retried safely.
        this.setState((previousState) => ({
          form: { ...previousState.form, uuid },
        }));
      }

      if (pendingUsageReports.length > 0) {
        const uploadResult = await this.uploadCpapFollowupFiles({
          fileType: "usageReports",
          files: pendingUsageReports
            .map((file) => file.localFile)
            .filter(Boolean),
          form: { ...this.state.form, uuid },
          inputRef: this.cpapReportImportRef,
          showSuccessAlert: false,
          refreshAfterUpload: false,
        });

        if (!uploadResult) {
          notifyWarning(
            "CPAP Follow-Up values were saved, but the CPAP Usage Report is still waiting to save. Please click Save again.",
          );
          return;
        }

        const pendingIds = new Set(
          pendingUsageReports.map((file) => file.id),
        );
        this.pendingCpapUsageReports = this.pendingCpapUsageReports.filter(
          (file) => !pendingIds.has(file.id),
        );
        payload.extendedProps.usageReports =
          uploadResult.patientFiles.usageReports;
        payload.extendedProps.questionnaires =
          uploadResult.patientFiles.questionnaires;
      }

      await this.addFollowupRecord(
        payload,
        isUpdate ? this.getFollowupRecordAction(payload, "update") : "create",
        currentUserName,
      );

      notifyTopRight(isUpdate ? "Update success" : "Create success");
      this.closeModal();
      await this.fetchData();
    } catch (error) {
      console.error(error);
      notifyError("Cannot save CPAP follow-up");
    }
  };

  onRemoveEvent = async () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    const { form } = this.state;

    if (!form.uuid) return;

    swal({
      title: "Are you sure to delete?",
      text: "Once deleted, you will not be able to recover this CPAP follow-up event.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        await deleteDoc(doc(db, COLLECTION_NAME, form.uuid));
        notifyTopRight("Delete success");
        this.closeModal();
        await this.fetchData();
      } catch (error) {
        console.error(error);
        notifyError("Delete error");
      }
    });
  };

  uploadCpapFollowupFiles = async ({
    fileType,
    files,
    form,
    inputRef,
    showSuccessAlert = true,
    refreshAfterUpload = true,
  }) => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return null;
    }

    if (!form.uuid) {
      notifyWarning("Please save the follow-up record before upload file");
      return null;
    }

    if (!form.hn) {
      notifyWarning("HN is empty");
      return null;
    }

    if (files.length === 0) {
      notifyWarning("No files selected");
      return null;
    }

    const isUsage = fileType === "usageReports";
    const folder = getCpapFollowupFileFolder(fileType);
    const uploadedFiles = [];
    const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
    const transferredByIndex = new Array(files.length).fill(0);

    const updateProgress = (fileName = "") => {
      const transferred = transferredByIndex.reduce((a, b) => a + b, 0);
      const pct =
        totalBytes > 0 ? Math.round((transferred / totalBytes) * 100) : 0;

      Alert.update({
        html: `
          <div style="text-align:left">
            <div><b>Overall:</b> ${pct}%</div>
            ${fileName
            ? `<div style="margin-top:6px"><b>Now:</b> ${fileName}</div>`
            : ""
          }
          </div>
        `,
      });
    };

    try {
      this.setState({ isUploading: true });

      Alert.fire({
        title: isUsage
          ? "Uploading CPAP usage report..."
          : "Uploading questionnaire...",
        html: "0%",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Alert.showLoading(),
      });

      const uploadTasks = files.map((file, index) => {
        return new Promise((resolve, reject) => {
          const safeName = `${Date.now()}_${file.name}`;
          const storagePath = `cpapfollowup/${form.hn}/${folder}/${safeName}`;
          const storageRef = ref(storage, storagePath);
          const task = uploadBytesResumable(storageRef, file);

          task.on(
            "state_changed",
            (snapshot) => {
              transferredByIndex[index] = snapshot.bytesTransferred;
              updateProgress(file.name);
            },
            (error) => reject(error),
            async () => {
              try {
                const url = await getDownloadURL(task.snapshot.ref);
                const fileData = buildCpapFollowupFileData({
                  file,
                  hn: form.hn,
                  fileType,
                  followupUuid: form.uuid,
                  userName: this.getCurrentUserName(),
                  savedFileName: safeName,
                  storagePath,
                  url,
                });
                const patientFileRef = await addDoc(
                  collection(db, "patients", form.hn, "cpapFollowupFiles"),
                  {
                    ...fileData,
                    uploadedAt: serverTimestamp(),
                  },
                );

                fileData.id = patientFileRef.id;
                uploadedFiles.push(fileData);
                resolve(fileData);
              } catch (error) {
                console.error("Save patient file metadata error:", error);

                try {
                  await deleteObject(task.snapshot.ref);
                } catch (cleanupError) {
                  console.error(
                    "Cleanup incomplete upload error:",
                    cleanupError,
                  );
                }

                reject(error);
              }
            },
          );
        });
      });

      await Promise.all(uploadTasks);

      const reloadedPatientFiles = await this.loadPatientCpapFiles(form.hn);
      const mergeFiles = (...fileLists) => {
        const fileMap = new Map();

        fileLists.forEach((fileList) => {
          (Array.isArray(fileList) ? fileList : []).forEach((fileData) => {
            if (!fileData) return;

            const key =
              fileData.storagePath ||
              fileData.id ||
              `${fileData.fileType || "file"}:${
                fileData.savedFileName || fileData.fileName || ""
              }`;

            fileMap.set(key, fileData);
          });
        });

        return Array.from(fileMap.values());
      };
      const patientFiles = {
        usageReports: mergeFiles(
          form.usageReports,
          reloadedPatientFiles.usageReports,
          uploadedFiles.filter(
            (fileData) => fileData.fileType === "usageReports",
          ),
        ),
        questionnaires: mergeFiles(
          form.questionnaires,
          reloadedPatientFiles.questionnaires,
          uploadedFiles.filter(
            (fileData) => fileData.fileType === "questionnaires",
          ),
        ),
      };

      await updateDoc(doc(db, COLLECTION_NAME, form.uuid), {
        "extendedProps.usageReports": patientFiles.usageReports,
        "extendedProps.questionnaires": patientFiles.questionnaires,
        updatedBy: this.getCurrentUserName(),
        updatedAt: serverTimestamp(),
      });

      Alert.close();

      if (showSuccessAlert) {
        swal("Upload success", { icon: "success" });
      }

      this.setState((prevState) => ({
        isUploading: false,
        form: {
          ...prevState.form,
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
        },
      }));

      if (inputRef && inputRef.current) inputRef.current.value = "";

      if (refreshAfterUpload) {
        await this.fetchData();
      }

      return {
        uploadedFiles,
        patientFiles,
      };
    } catch (error) {
      console.error(error);

      try {
        Alert.close();
      } catch (_) { }

      this.setState({ isUploading: false });
      notifyError("Upload error");
      return null;
    }
  };

  uploadFiles = async (fileType) => {
    const isUsage = fileType === "usageReports";
    const inputRef = isUsage ? this.usageFileRef : this.questionnaireFileRef;
    const files =
      inputRef && inputRef.current && inputRef.current.files
        ? Array.from(inputRef.current.files)
        : [];

    return this.uploadCpapFollowupFiles({
      fileType,
      files,
      form: this.state.form,
      inputRef,
    });
  };

  openFile = async (fileData) => {
    try {
      const url = await this.getFileUrl(fileData);

      if (!url) {
        notifyError("Cannot open file");
        return;
      }

      const previewFile = {
        ...fileData,
        url: url,
        extension: this.getFileExtension(fileData),
      };

      this.setState({
        previewFile: previewFile,
        isFilePreviewModal: true,
      });
    } catch (error) {
      console.error(error);
      notifyError("Cannot open file");
    }
  };

  openFileNewTab = async () => {
    try {
      const fileData = this.state.previewFile;

      if (!fileData) return;

      const url = await this.getFileUrl(fileData);

      if (url) {
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error(error);
      notifyError("Cannot open file");
    }
  };

  closeFilePreviewModal = () => {
    this.setState({
      isFilePreviewModal: false,
      previewFile: null,
    });
  };

  deleteUploadedFile = async (fileType, fileData) => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    const { form } = this.state;

    if (!form.uuid) {
      notifyWarning("Please save the follow-up record first");
      return;
    }

    if (!form.hn) {
      notifyWarning("HN is empty");
      return;
    }

    swal({
      title: "Delete this file?",
      text: "This will remove the file from this CPAP follow-up and patient file record.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        const currentList = Array.isArray(form[fileType]) ? form[fileType] : [];

        const nextFileList = currentList.filter((item) => {
          if (fileData.id && item.id) return item.id !== fileData.id;
          if (fileData.storagePath && item.storagePath) {
            return item.storagePath !== fileData.storagePath;
          }
          return item.fileName !== fileData.fileName;
        });

        // 1) Remove from cpap-followup event extendedProps
        await updateDoc(doc(db, COLLECTION_NAME, form.uuid), {
          [`extendedProps.${fileType}`]: nextFileList,
          updatedBy: this.getCurrentUserName(),
          updatedAt: serverTimestamp(),
        });

        // 2) Remove from patient cpapFollowupFiles metadata
        if (fileData.id) {
          try {
            await deleteDoc(
              doc(db, "patients", form.hn, "cpapFollowupFiles", fileData.id),
            );
          } catch (error) {
            console.warn("Delete patient file metadata error:", error);
          }
        }

        // 3) Remove actual file from Firebase Storage
        if (fileData.storagePath) {
          try {
            await deleteObject(ref(storage, fileData.storagePath));
          } catch (error) {
            console.warn("Delete storage file error:", error);
          }
        }

        notifyTopRight("File deleted");

        const patientFiles = await this.loadPatientCpapFiles(form.hn);

        this.setState((prevState) => ({
          form: {
            ...prevState.form,
            usageReports: patientFiles.usageReports,
            questionnaires: patientFiles.questionnaires,
          },
        }));

        await this.fetchData();
      } catch (error) {
        console.error(error);
        notifyError("Delete file error");
      }
    });
  };

  renderTextInput = (label, name, placeholder = "", type = "text") => {
    return (
      <div className="form-group row mb-1">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8">
          <input
            type={type}
            className="form-control form-control-lg"
            placeholder={placeholder || label}
            value={safeText(this.state.form[name])}
            onChange={(e) => this.handleInputChange(name, e.target.value)}
            disabled={!this.state.isEditMode}
            style={{
              height: 28,
              minHeight: 28,
              fontSize: 12,
              padding: "3px 8px",
            }}
          />
        </div>
      </div>
    );
  };
  renderTextArea = (label, name, rows = 3, width = "4") => {
    const labelWidth = Number(width);
    const isFullWidth = labelWidth >= 12;

    return (
      <div className="form-group row mb-1">
        <label
          className={`col-lg-${isFullWidth ? 12 : width} col-form-label`}
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className={`col-lg-${isFullWidth ? 12 : 12 - width}`}>
          <textarea
            className="form-control form-control-lg"
            rows={rows}
            value={safeText(this.state.form[name])}
            onChange={(e) => this.handleInputChange(name, e.target.value)}
            disabled={!this.state.isEditMode}
            style={{
              fontSize: 12,
              padding: "4px 8px",
              lineHeight: 1.35,
            }}
          />
        </div>
      </div>
    );
  };
  renderSelect = (label, name, options, onChange = null) => {
    return (
      <div className="form-group row mb-1">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8">
          <select
            className="form-control form-control-lg"
            value={safeText(this.state.form[name])}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              else this.handleInputChange(name, e.target.value);
            }}
            disabled={!this.state.isEditMode}
            style={{
              height: 28,
              minHeight: 28,
              fontSize: 12,
              padding: "3px 8px",
            }}
          >
            {options.map((option) => {
              if (typeof option === "string") {
                return (
                  <option key={option} value={option}>
                    {option}
                  </option>
                );
              }

              return (
                <option key={option.value} value={option.value}>
                  {option.label || option.value}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    );
  };
  renderRadioGroup = (label, name, options) => {
    return (
      <div className="form-group row mb-2">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8" style={{ fontSize: 12 }}>
          {options.map((option) => (
            <label key={option} className="mr-3 mb-1">
              <input
                type="radio"
                className="mr-1"
                checked={this.state.form[name] === option}
                onChange={() => this.handleInputChange(name, option)}
                disabled={!this.state.isEditMode}
              />
              {option}
            </label>
          ))}
        </div>
      </div>
    );
  };
  renderSectionTitle = (title, subtitle = "") => (
    <div className="mb-2 mt-2">
      <h6 className="mb-0" style={{ fontWeight: 700, fontSize: 13 }}>
        {title}
      </h6>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
  );

  renderTabs = () => {
    const tabs = [
      { key: "patient", label: "Patient" },
      { key: "device", label: "Device" },
      { key: "therapy", label: "Therapy Data" },
      { key: "symptoms", label: "Symptoms" },
      { key: "annualMaskPurchase", label: "Annual Mask Purchase" },
    ];

    return (
      <ul className="nav nav-tabs mb-3">
        {tabs.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${this.state.activeTab === tab.key ? "active" : ""
                }`}
              onClick={() => this.setState({ activeTab: tab.key })}
              style={{ cursor: "pointer" }}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  renderAuditInfo = () => {
    const { form } = this.state;

    const createdText =
      form._createdBy || form._createdAt
        ? `${form._createdBy || "-"} · ${this.formatAuditDateTime(
          form._createdAt,
        )}`
        : "-";

    const updatedText =
      form._updatedBy || form._updatedAt
        ? `${form._updatedBy || "-"} · ${this.formatAuditDateTime(
          form._updatedAt,
        )}`
        : "-";

    return (
      <div
        style={{
          fontSize: 11,
          color: "#6c757d",
          lineHeight: 1.35,
          textAlign: "right",
          maxWidth: 360,
          marginRight: 12,
        }}
      >
        <div>
          <span style={{ fontWeight: 600 }}>Created:</span> {createdText}
        </div>
        <div>
          <span style={{ fontWeight: 600 }}>Updated:</span> {updatedText}
        </div>
      </div>
    );
  };
  renderLatestTherapyStatus = () => {
    const original = this.state.form.originalTherapyStatus;
    const latest = this.state.form.latestTherapyStatus;
    const savedRecord = this.state.form.latestTherapyStatusRecord;

    const hasOriginal =
      original &&
      (original.type ||
        original.status ||
        original.doctorActionStatus ||
        original.note);

    const hasChanged =
      hasOriginal &&
      (original.type !== this.state.form.type ||
        original.status !== this.state.form.status ||
        original.doctorActionStatus !== this.state.form.doctorActionStatus ||
        original.note !== this.state.form.note);

    let title = "Previous therapy status";
    let dataToShow = null;

    if (hasChanged) {
      title = "Before edit";
      dataToShow = original;
    } else if (latest) {
      title = "Previous therapy status";
      dataToShow = latest;
    } else if (savedRecord) {
      if (savedRecord.source === "doctor_decision_before_reschedule") {
        title = "Previous doctor decision";
      } else if (savedRecord.source === "before_edit") {
        title = "Latest therapy";
      } else {
        title = "Saved previous therapy";
      }

      dataToShow = savedRecord;
    }

    if (!dataToShow) {
      return (
        <div
          className="form-group row mb-2"
          style={{
            marginTop: 4,
          }}
        >
          <label className="col-lg-4 col-form-label"></label>
          <div className="col-lg-8">
            <small className="text-muted">
              Previous therapy: no previous follow-up record
            </small>
          </div>
        </div>
      );
    }

    return (
      <div
        className="form-group row mb-2"
        style={{
          marginTop: 4,
        }}
      >
        <label className="col-lg-4 col-form-label"></label>
        <div className="col-lg-8">
          <div
            style={{
              fontSize: 11,
              color: "#6c757d",
              background: "#f8f9fa",
              border: "1px solid #eef0f2",
              borderRadius: 6,
              padding: "8px 10px",
              lineHeight: 1.45,
            }}
          >
            <div style={{ fontWeight: 700, color: "#495057", marginBottom: 2 }}>
              {title}
            </div>

            <div>Date: {dataToShow.date || "-"}</div>
            <div>Type: {dataToShow.type || "-"}</div>
            <div>Status: {dataToShow.status || "-"}</div>
            <div>Min Pressure: {dataToShow.minPressure || "-"}</div>
            <div>Max Pressure: {dataToShow.maxPressure || "-"}</div>
            <div>Current Ramp Time: {dataToShow.currentRampTime || "-"}</div>
            <div>
              Doctor Next Step:{" "}
              {this.getDoctorActionLabel(dataToShow.doctorActionStatus)}
            </div>

            <div
              style={{
                marginTop: 6,
                paddingTop: 6,
                borderTop: "1px solid #e9ecef",
              }}
            >
              <div style={{ fontWeight: 600, color: "#495057" }}>
                Previous Note
              </div>
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  color: dataToShow.note ? "#6c757d" : "#adb5bd",
                }}
              >
                {dataToShow.note || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderSummaryHeader = () => {
    const { form } = this.state;
    const statusConfig = getStatusConfig(form.status);

    const doctorAction = this.getDoctorActionLabel(form.doctorActionStatus);

    const patientName = `${safeText(form.firstname)} ${safeText(
      form.lastname,
    )}`.trim();

    return (
      <div
        className="p-3 mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: "12px",
          background: "#f8f9fa",
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div style={{ minWidth: 0, flex: 1, paddingRight: 16 }}>
            <h5
              className="mb-1"
              style={{
                fontWeight: 700,
                color: "#2f365f",
                lineHeight: 1.25,
              }}
            >
              {form.order ? form.order : "New CPAP Follow-Up"}

              {patientName && (
                <small
                  className="text-muted ml-2"
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {patientName}
                </small>
              )}
            </h5>

            <div
              className="text-muted small"
              style={{
                lineHeight: 1.6,
              }}
            >
              <div>
                {form.hospitalRef ? (
                  <span>Ref HN: {form.hospitalRef}</span>
                ) : (
                  <span>Ref HN: -</span>
                )}
              </div>

              <div>
                <span>Node: {form.node || "-"}</span>
              </div>

              <div>
                <span>Customer: {form.customer || "-"}</span>
              </div>
            </div>
          </div>

          <div
            className="text-right"
            style={{
              minWidth: 230,
              flex: "0 0 auto",
            }}
          >
            <Badge
              variant={statusConfig.badge}
              className="mb-2"
              style={{
                backgroundColor: statusConfig.color,
                color: "#3b2b46",
                borderRadius: 12,
                padding: "7px 12px",
                fontSize: 12,
              }}
            >
              {statusConfig.label}
            </Badge>

            <div className="small text-muted">{form.type || "-"}</div>
            <div className="small text-muted">
              Doctor: {doctorAction || "-"}
            </div>

            {/* {this.renderAuditInfo()} */}
          </div>
        </div>
      </div>
    );
  };

  renderFilePreviewModal = () => {
    const file = this.state.previewFile;

    if (!this.state.isFilePreviewModal || !file) return null;

    const extension = this.getFileExtension(file);
    const url = file.url || "";
    const lowerExt = extension.toLowerCase();

    const isPdf = lowerExt === "pdf";

    const isImage =
      lowerExt === "jpg" ||
      lowerExt === "jpeg" ||
      lowerExt === "png" ||
      lowerExt === "gif" ||
      lowerExt === "webp" ||
      lowerExt === "bmp";

    const isWord =
      lowerExt === "doc" ||
      lowerExt === "docx" ||
      lowerExt === "rtf" ||
      lowerExt === "odt";

    let previewContent = null;

    if (isPdf) {
      previewContent = (
        <iframe
          title="PDF Preview"
          src={url}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            backgroundColor: "#fff",
          }}
        />
      );
    } else if (isImage) {
      previewContent = (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
        >
          <img
            src={url}
            alt={file.fileName || "Preview"}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      );
    } else if (isWord) {
      const officeViewerUrl =
        "https://view.officeapps.live.com/op/embed.aspx?src=" +
        encodeURIComponent(url);

      previewContent = (
        <iframe
          title="Word Preview"
          src={officeViewerUrl}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            backgroundColor: "#fff",
          }}
        />
      );
    } else {
      previewContent = (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h5 style={{ marginBottom: 8 }}>Preview not available</h5>
          <p style={{ color: "#6c757d", marginBottom: 16 }}>
            This file type cannot be previewed in the browser.
          </p>
          <Button variant="primary" onClick={this.openFileNewTab}>
            Open File
          </Button>
        </div>
      );
    }

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          backgroundColor: "rgba(0,0,0,0.72)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1vh 1vw",
        }}
      >
        <div
          style={{
            width: "98vw",
            height: "98vh",
            backgroundColor: "#fff",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          }}
        >
          <div
            style={{
              minHeight: 58,
              padding: "8px 14px",
              borderBottom: "1px solid #e9ecef",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              backgroundColor: "#fff",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>File Preview</div>
              <div
                style={{
                  fontSize: 12,
                  color: "#6c757d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={file.fileName || file.storagePath || ""}
              >
                {file.fileName || file.storagePath || ""}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: "0 0 auto",
              }}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={this.openFileNewTab}
              >
                Open New Tab
              </Button>

              <Button
                variant="light"
                size="sm"
                onClick={this.closeFilePreviewModal}
                style={{
                  border: "1px solid #ced4da",
                  minWidth: 38,
                }}
              >
                ✕
              </Button>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              backgroundColor: "#f8f9fa",
              overflow: "hidden",
            }}
          >
            {previewContent}
          </div>
        </div>
      </div>
    );
  };

  renderUploadReportSection = ({
    title,
    files,
    fileType,
    inputRef,
    uploadLabel = "+ Upload",
    showUploadButton = true,
  }) => {
    const canUpload = Boolean(this.state.form.uuid) && !this.state.isUploading;
    const role = this.getCurrentRole();
    const isBlockedRole =
      ["sale", "accountant", "staff"].includes(role) ||
      this.isStaffReadOnly();

    const handleUploadClick = () => {
      if (!this.state.form.uuid) {
        notifyWarning("Please save the follow-up record before upload file");
        return;
      }

      if (inputRef.current) {
        inputRef.current.click();
      }
    };

    const renderUploadButton = () => {
      if (isBlockedRole) return null;

      return (
        <>
          <Button
            onClick={handleUploadClick}
            className="btn-xxs btn-primary"
            disabled={!canUpload}
          >
            {uploadLabel}
          </Button>

          <input
            type="file"
            multiple
            ref={inputRef}
            style={{ display: "none" }}
            onChange={() => this.uploadFiles(fileType)}
          />
        </>
      );
    };

    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: 12,
          background: "#fff",
        }}
      >
        {/* <div
          className="d-flex justify-content-between align-items-center mb-2"
          style={{ gap: 12 }}
        >
          <div>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <small className="text-muted">
              {this.state.form.uuid
                ? "Upload and open file from patient record"
                : "Save this follow-up record before upload file"}
            </small>
          </div>

          {(!files || files.length === 0) && renderUploadButton()}
        </div> */}
        <div
          className="d-flex justify-content-between align-items-center mb-2"
          style={{ gap: 12 }}
        >
          <div>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <small className="text-muted">
              {showUploadButton
                ? "Upload and open file from patient record"
                : "Open file from patient record"}
            </small>
          </div>

          {showUploadButton && (
            <Fragment>
            <Button
              onClick={handleUploadClick}
              className="btn-xxs btn-primary"
              disabled={!canUpload}
            >
              {this.state.isUploading ? "Uploading..." : uploadLabel}
            </Button>

            <input
              type="file"
              multiple
              ref={inputRef}
              style={{ display: "none" }}
              onChange={() => this.uploadFiles(fileType)}
            />
            </Fragment>
          )}
        </div>

        {files && files.length > 0 ? (
          <div>
            {files.map((file, fileIndex) => (
              <div
                key={`${file.storagePath || file.fileName}-${fileIndex}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 42px 90px",
                  // gridTemplateColumns: "minmax(0, 1fr) 42px",
                  alignItems: "center",
                  gap: 12,
                  padding: "6px 0",
                  width: "100%",
                  borderTop: fileIndex === 0 ? "0" : "1px solid #f1f1f1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    gap: 8,
                  }}
                >
                  <i
                    className="flaticon-381-file-2"
                    style={{
                      color: "#dd0102",
                      flex: "0 0 auto",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => this.openFile(file)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      minWidth: 0,
                      textAlign: "left",
                      cursor: "pointer",
                      width: "100%",
                    }}
                    title={file.fileName}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%",
                        color: "#007bff",
                        fontSize: 12,
                        lineHeight: 1.25,
                      }}
                    >
                      {file.fileName}
                    </span>

                    <small
                      className="text-muted"
                      style={{
                        display: "block",
                        fontSize: 10,
                        lineHeight: 1.25,
                        marginTop: 2,
                      }}
                    >
                      Uploaded: {this.formatFileUploadDate(file.uploadedAt)}
                      {file.uploadedBy ? ` by ${file.uploadedBy}` : ""}
                    </small>
                  </button>{" "}
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    type="button"
                    className="btn btn-info shadow btn-xs sharp"
                    onClick={() => this.openFile(file)}
                    title="Preview file"
                  >
                    <i className="fa fa-eye" />
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {!isBlockedRole && this.state.isEditMode ? (
                    <button
                      type="button"
                      className="btn btn-danger shadow btn-xs sharp"
                      onClick={() => this.deleteUploadedFile(fileType, file)}
                      title="Delete file"
                      disabled={this.state.isUploading}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  ) : (
                    <span />
                  )}
                </div>

                {/* <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {fileIndex === 0 ? renderUploadButton() : <span />}
                </div> */}
              </div>
            ))}
          </div>
        ) : (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              paddingTop: 6,
              borderTop: "1px solid #f1f1f1",
            }}
          >
            <div className="text-muted small">No Report</div>
          </div>
        )}
      </div>
    );
  };

  renderPendingCpapUsageReportSection = () => {
    const files = this.pendingCpapUsageReports;
    if (!files.length) return null;

    const canRemove =
      this.state.isEditMode &&
      !this.isStaffReadOnly() &&
      !this.state.isUploading;

    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #ffc107",
          borderRadius: 8,
          padding: 12,
          background: "#fffdf4",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div style={{ fontWeight: 700 }}>CPAP Usage Report</div>
            <small className="text-muted">
              The Therapy copy is local until this CPAP Follow-Up is saved.
            </small>
          </div>
          <Badge variant="warning">Waiting to save</Badge>
        </div>

        {files.map((file, index) => (
          <div
            key={file.id || `${file.fileName}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto 30px",
              alignItems: "center",
              gap: 8,
              padding: "7px 0",
              borderTop: index === 0 ? "0" : "1px solid #f1e4b1",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 8,
              }}
            >
              <i
                className="flaticon-381-file-2"
                style={{ color: "#dd0102", flex: "0 0 auto" }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                    lineHeight: 1.25,
                  }}
                  title={file.fileName}
                >
                  {file.fileName}
                </div>
                <small className="text-muted" style={{ fontSize: 10 }}>
                  Added: {this.formatFileUploadDate(file.queuedAt)}
                </small>
              </div>
            </div>

            <small className="text-warning" style={{ fontSize: 10 }}>
              Not uploaded
            </small>

            {canRemove ? (
              <button
                type="button"
                className="btn btn-link btn-xs p-0 text-danger"
                onClick={() => this.removePendingCpapUsageReport(file.id)}
                title="Remove pending file"
              >
                <i className="fa fa-times" />
              </button>
            ) : (
              <span />
            )}
          </div>
        ))}
      </div>
    );
  };

  renderCpapReportImportSection = () => {
    const selectedType =
      this.state.form.cpapReportType || this.inferCpapReportType();

    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: 12,
          background: "#fff",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center mb-2"
          style={{ gap: 12 }}
        >
          <div>
          <div style={{ fontWeight: 700 }}>Import CPAP Report PDF</div>
          <small className="text-muted">
            Fill Therapy fields now. The Therapy copy uploads after you click Save.
          </small>
          </div>

          <Button
            onClick={this.handleCpapReportImportClick}
            className="btn-xxs btn-primary"
            disabled={
              !this.state.isEditMode ||
              this.state.isImportingCpapReport ||
              this.state.isUploading
            }
          >
            {this.state.isImportingCpapReport ? "Reading..." : "+ PDF"}
          </Button>
        </div>

        <div className="form-group row mb-0">
          <label
            className="col-lg-4 col-form-label"
            style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
          >
            Report Type
          </label>
          <div className="col-lg-8">
            <select
              className="form-control form-control-lg"
              value={selectedType}
              onChange={(e) =>
                this.handleInputChange("cpapReportType", e.target.value)
              }
              disabled={!this.state.isEditMode}
              style={{
                height: 28,
                minHeight: 28,
                fontSize: 12,
                padding: "3px 8px",
              }}
            >
              {CPAP_REPORT_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {type
                    .replace("Resmed", "ResMed")
                    .replace("Airsense", "AirSense")
                    .replace("Airstart", "AirStart")}
                </option>
              ))}
            </select>
          </div>
        </div>

        <input
          type="file"
          accept="application/pdf,.pdf"
          ref={this.cpapReportImportRef}
          style={{ display: "none" }}
          onChange={this.handleCpapReportFileChange}
        />
      </div>
    );
  };

  renderSymptomPdfImportSection = () => (
    <div
      className="mb-3"
      style={{
        border: "1px solid #e9ecef",
        borderRadius: 8,
        padding: 12,
        background: "#fff",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ gap: 12 }}
      >
        <div>
          <div style={{ fontWeight: 700 }}>Import Symptom PDF</div>
          <small className="text-muted">
            Fill ESS, symptoms, side effects, and other note from Google Forms
            PDF, then upload it to Questionnaire Files
          </small>
        </div>

        <Button
          onClick={this.handleSymptomPdfImportClick}
          className="btn-xxs btn-primary"
          disabled={!this.state.isEditMode || this.state.isImportingSymptomPdf}
        >
          {this.state.isImportingSymptomPdf ? "Reading..." : "+ PDF"}
        </Button>
      </div>

      <input
        type="file"
        accept="application/pdf,.pdf"
        ref={this.symptomPdfImportRef}
        style={{ display: "none" }}
        onChange={this.handleSymptomPdfFileChange}
      />
    </div>
  );

  renderPatientTab = () => {
    const nodeOptions = [
      "กรุณาเลือก",
      ...this.state.nodeList.map((item) => item.nodename),
    ];

    const customerOptions = [
      "กรุณาเลือก",
      ...this.state.customerList.map((item) => item.displayName || item.id),
    ];

    return (
      <Row>
        <Col lg={6}>
          {this.renderSectionTitle("Patient", "Basic patient information")}
          {this.renderTextInput("Order", "order")}

          {this.renderSelect(
            "Node",
            "node",
            nodeOptions,
            this.handleNodeChange,
          )}
          {this.renderSelect("Customer", "customer", customerOptions)}
          <div className="form-group row mb-1">
            <label
              className="col-lg-4 col-form-label"
              style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
            >
              HN
            </label>

            <div className="col-lg-5">
              <input
                type="text"
                className="form-control form-control-sm"
                value={this.state.form.hn}
                onChange={(e) => this.handleInputChange("hn", e.target.value)}
                disabled={!this.state.isEditMode}
                style={{
                  height: 28,
                  minHeight: 28,
                  fontSize: 12,
                  padding: "3px 8px",
                  lineHeight: "20px",
                }}
              />
            </div>

            <div className="col-lg-3">
              <Button
                className="btn btn-secondary btn-sm w-100"
                onClick={this.onSearchPatient}
                disabled={!this.state.isEditMode}
                style={{
                  height: 28,
                  minHeight: 28,
                  fontSize: 12,
                  padding: "3px 8px",
                  lineHeight: "18px",
                }}
              >
                Search
              </Button>
            </div>
          </div>
          {this.state.isNewPatient && (
            <div className="form-group row mb-2">
              <label className="col-lg-4 col-form-label"></label>
              <div className="col-lg-8 text-danger small">* New Patient</div>
            </div>
          )}

          {this.renderTextInput("Firstname", "firstname")}
          {this.renderTextInput("Lastname", "lastname")}
          {this.renderTextInput("Ref HN / Hospital Ref", "hospitalRef")}
          {this.renderTextInput("Phone", "phone")}
          {this.renderTextInput(
            "AHI ก่อนใช้เครื่อง",
            "prePurchaseAhi",
            "AHI",
            "number",
          )}
        </Col>

        <Col lg={6}>
          {this.renderSectionTitle(
            "Follow-Up",
            "Schedule, status, and responsible staff",
          )}

          <div className="form-group row mb-1">
            <label
              className="col-lg-4 col-form-label"
              style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
            >
              Date
            </label>

            <div className="col-lg-8">
              <DatePicker
                dateFormat="yyyy-MM-dd"
                selected={
                  this.state.form.date
                    ? new Date(`${this.state.form.date}T00:00:00`)
                    : new Date()
                }
                onChange={(date) =>
                  this.handleInputChange(
                    "date",
                    date ? formatLocalDate(date) : this.state.form.date,
                  )
                }
                disabled={!this.state.isEditMode}
                wrapperClassName="w-100"
                customInput={
                  <input
                    className="form-control form-control-sm"
                    style={{
                      height: 28,
                      minHeight: 28,
                      fontSize: 12,
                      padding: "3px 8px",
                      lineHeight: "20px",
                    }}
                  />
                }
              />
            </div>
          </div>
          {/* {this.renderTextInput("Time", "time", "09:00")} */}
          {this.renderSelect(
            "Follow-Up Type",
            "type",
            getFollowupTypeOptionsForNode(
              this.state.form.node,
              this.state.nodeList,
            ),
          )}
          {this.renderSelect(
            "Status",
            "status",
            STATUS_OPTIONS.map((item) => ({
              value: item.value,
              label: item.label,
            })),
          )}

          <div className="form-group row mb-2">
            <label className="col-lg-4 col-form-label"></label>
            <div className="col-lg-8">
              <small className="text-muted">
                {getStatusConfig(this.state.form.status).helper}
              </small>
            </div>
          </div>

          {this.renderSelect(
            "Technician Consult",
            "technicianConsult",
            this.state.technicianList,
          )}
          {this.renderSelect(
            "Doctor Consult",
            "doctorConsult",
            this.state.doctorList,
          )}
          {this.renderSelect(
            "Doctor Next Step",
            "doctorActionStatus",
            DOCTOR_ACTION_OPTIONS,
            this.handleDoctorActionChange,
          )}
          <div style={{ height: 4 }} />
          {this.renderLatestTherapyStatus()}
        </Col>

        <Col lg={12} className="mt-3">
          {this.renderSectionTitle("Files & Note")}

          <Row>
            <Col lg={6}>
              {this.renderUploadReportSection({
                title: "CPAP Usage Report",
                files: this.state.form.usageReports,
                fileType: "usageReports",
                inputRef: this.usageFileRef,
                uploadLabel: "+ Upload",
              })}
            </Col>

            <Col lg={6}>
              {this.renderUploadReportSection({
                title: "Questionnaire",
                files: this.state.form.questionnaires,
                fileType: "questionnaires",
                inputRef: this.questionnaireFileRef,
                uploadLabel: "+ Upload",
              })}
            </Col>

            <Col lg={12} className="mt-2">
              {this.renderReadOnlyFileListSection({
                title: "Sleep Study Reports",
                files: this.state.form.sleepStudyReports,
                emptyText: "No Sleep Study Report",
              })}
            </Col>
          </Row>

          {this.renderTextArea("Note", "note", 4, "2")}
          {this.renderNoteCard({
            title: "Previous Note",
            date:
              this.state.form.latestTherapyStatusRecord &&
              (this.state.form.latestTherapyStatusRecord.date ||
                this.state.form.latestTherapyStatusRecord.recordedAt),
            note:
              this.state.form.latestTherapyStatusRecord &&
              this.state.form.latestTherapyStatusRecord.note,
            mutedText: "No previous note",
          })}
        </Col>
      </Row>
    );
  };

  renderDeviceTab = () => (
    <Row>
      <Col lg={6}>
        {this.renderSectionTitle("CPAP Device", "Device and mask information")}
        {this.renderTextInput("Serial Number", "serialNumber")}
        {this.renderTextInput("Brand", "brand")}
        {this.renderTextInput("Model", "model")}
        {this.renderSelect("CPAP Type", "cpapType", CPAP_TYPE_OPTIONS)}
        {this.renderTextInput("EPR", "epr")}
        {this.renderSelect("Mask Type", "maskType", MASK_TYPE_OPTIONS)}
        {this.renderSelect("Mask Size", "maskSize", MASK_SIZE_OPTIONS)}
      </Col>
    </Row>
  );

  renderTherapyTab = () => (
    <Row>
      <Col lg={12}>{this.renderCpapReportImportSection()}</Col>

      <Col lg={12}>
        <Row>
          <Col lg={6}>
            {this.renderUploadReportSection({
              title: "CPAP Usage Report",
              files: this.state.form.usageReports,
              fileType: "usageReports",
              inputRef: this.usageFileRef,
              uploadLabel: "+ Upload",
              showUploadButton: false,
            })}
            {this.renderPendingCpapUsageReportSection()}
          </Col>
        </Row>
      </Col>

      <Col lg={6}>
        {this.renderSectionTitle("CPAP Pressure Setting")}
        {this.renderTextInput("Min Pressure", "minPressure", "cmH2O", "number")}
        {this.renderTextInput("Max Pressure", "maxPressure", "cmH2O", "number")}
        {this.renderTextInput(
          "Current Ramp Time",
          "currentRampTime",
          "minutes",
          "number",
        )}
        {this.renderTextInput("Humidifier System", "humidifierSystem")}
        {this.renderTextInput("Average Leak", "averageLeak")}

        {this.renderSectionTitle("Objective Data")}
        {this.renderTextInput("Data Period", "dataPeriod")}
        {this.renderTextInput("Date Summary", "dateSummary")}
      </Col>

      <Col lg={6}>
        {this.renderSectionTitle("Compliance")}
        {this.renderTextInput(
          "% การใช้งานที่มากกว่า 4 ชม.",
          "usageMoreThan4HoursPercent",
          "%",
          "number",
        )}
        {this.renderTextInput(
          "ชั่วโมงการใช้งานเฉลี่ย",
          "averageUsageHours",
          "hours/night",
          "number",
        )}

        {this.renderSectionTitle("Efficiency")}
        {this.renderTextInput(
          "AHI เฉลี่ยต่อ ชม",
          "averageAhi",
          "events/hour",
          "number",
        )}
        {this.renderTextInput(
          "แรงดัน 95% สำหรับ APAP",
          "pressure95Percent",
          "cmH2O",
          "number",
        )}

        {this.renderSectionTitle("Mask Leak")}
        {this.renderTextInput("Leak Rate", "leakRate")}
        {this.renderSelect(
          "การประเมิน",
          "leakAssessment",
          LEAK_ASSESSMENT_OPTIONS,
        )}
      </Col>
    </Row>
  );

  renderSymptomsTab = () => (
    <Fragment>
      {this.renderSymptomPdfImportSection()}
      <Row>
        <Col lg={6}>
          {this.renderUploadReportSection({
            title: "Questionnaire",
            files: this.state.form.questionnaires,
            fileType: "questionnaires",
            inputRef: this.questionnaireFileRef,
            uploadLabel: "+ Upload",
            showUploadButton: false,
          })}
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
        {this.renderSectionTitle("Questionnaire")}
        {this.renderTextInput(
          "ข้อมูลคะแนนความง่วง ESS",
          "essScore",
          "ESS score",
          "number",
        )}
        {this.renderTextArea("โรคประจำตัว", "underlyingDisease", 2, "4")}
        {this.renderTextInput(
          "น้ำหนักปัจจุบัน",
          "currentWeight",
          "เช่น 72 kg",
        )}
        {this.renderTextInput("การเปลี่ยนแปลงน้ำหนัก", "weightChange")}

        {this.renderSectionTitle("Overall Symptoms")}
        {this.renderRadioGroup(
          "ความรู้สึกหลังตื่นนอน",
          "wakeFeeling",
          WAKE_FEELING_OPTIONS,
        )}
        {this.renderRadioGroup(
          "อาการง่วงนอนตอนกลางวัน",
          "daytimeSleepiness",
          DAYTIME_SLEEPINESS_OPTIONS,
        )}
        {this.renderTextArea("อื่นๆ", "sideEffectsOther", 4, "12")}
      </Col>

      <Col lg={6}>
        {this.renderSectionTitle("ปัญหาและผลข้างเคียงที่พบ")}
        <div className="pl-2">
          {getCpapSideEffectOptionsForDisplay().map((option) => (
            <div className="form-check mb-2" key={option}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`side-effect-${option}`}
                checked={this.state.form.sideEffects.includes(option)}
                onChange={(e) =>
                  this.handleSideEffectChange(option, e.target.checked)
                }
                disabled={!this.state.isEditMode}
              />
              <label
                className="form-check-label"
                htmlFor={`side-effect-${option}`}
              >
                {option}
              </label>
            </div>
          ))}
          {getSavedNonPdfCpapSideEffectValues(
            this.state.form.sideEffects,
          ).length > 0 && (
            <small className="text-muted d-block mt-2">
              ค่าที่บันทึกไว้จากรายการเดิม (เก็บไว้โดยไม่แก้ไข):{" "}
              {getSavedNonPdfCpapSideEffectValues(
                this.state.form.sideEffects,
              ).join(", ")}
            </small>
          )}
        </div>
        </Col>
      </Row>
    </Fragment>
  );

  handleAnnualMaskCaseLinked = (annualMaskPurchase) => {
    this.setState((previousState) => ({
      form: {
        ...previousState.form,
        annualMaskPurchase,
      },
    }));
  };

  renderAnnualMaskPurchaseTab = () => (
    <AnnualMaskPurchaseEventSection
      followup={this.state.form}
      pendingUsageReports={this.pendingCpapUsageReports}
      readOnly={!this.state.isEditMode || this.isStaffReadOnly()}
      onCaseLinked={this.handleAnnualMaskCaseLinked}
    />
  );

  renderActiveTab = () => {
    switch (this.state.activeTab) {
      case "device":
        return this.renderDeviceTab();
      case "therapy":
        return this.renderTherapyTab();
      case "symptoms":
        return this.renderSymptomsTab();
      case "annualMaskPurchase":
        return this.renderAnnualMaskPurchaseTab();
      case "patient":
      default:
        return this.renderPatientTab();
    }
  };

  formatFileUploadDate = (value) => {
    if (!value) return "-";

    let dateValue = null;

    if (value.toDate) {
      dateValue = value.toDate();
    } else if (value.seconds) {
      dateValue = new Date(value.seconds * 1000);
    } else if (value instanceof Date) {
      dateValue = value;
    } else {
      dateValue = new Date(value);
    }

    if (!dateValue || isNaN(dateValue.getTime())) return "-";

    return dateValue.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  uploadEditFiles = async (fileType) => {
    const isUsage = fileType === "usageReports";
    const inputRef = isUsage ? this.usageFileRef : this.questionnaireFileRef;

    const files =
      inputRef && inputRef.current && inputRef.current.files
        ? Array.from(inputRef.current.files)
        : [];

    const { editForm } = this.state;

    if (!editForm || !editForm.uuid) {
      notifyWarning("Please save the follow-up record before upload file");
      return;
    }

    if (!editForm.hn) {
      notifyWarning("HN is empty");
      return;
    }

    if (files.length === 0) {
      notifyWarning("No files selected");
      return;
    }

    const folder = isUsage ? "reports" : "questionnaires";

    try {
      this.setState({ isUploading: true });

      const uploadTasks = files.map((file) => {
        return new Promise((resolve, reject) => {
          const safeName = `${Date.now()}_${file.name}`;
          const storagePath = `cpapfollowup/${editForm.hn}/${folder}/${safeName}`;
          const storageRef = ref(storage, storagePath);
          const task = uploadBytesResumable(storageRef, file);

          task.on(
            "state_changed",
            () => { },
            (error) => reject(error),
            async () => {
              try {
                const url = await getDownloadURL(task.snapshot.ref);

                const fileData = {
                  fileName: file.name,
                  savedFileName: safeName,
                  storagePath,
                  url,
                  uploadedAt: new Date().toISOString(),
                  uploadedBy: this.getCurrentUserName(),
                  hn: editForm.hn,
                  fileType,
                  followupUuid: editForm.uuid,
                };

                const patientFileRef = await addDoc(
                  collection(db, "patients", editForm.hn, "cpapFollowupFiles"),
                  {
                    ...fileData,
                    uploadedAt: serverTimestamp(),
                  },
                );

                resolve({
                  ...fileData,
                  id: patientFileRef.id,
                });
              } catch (error) {
                reject(error);
              }
            },
          );
        });
      });

      await Promise.all(uploadTasks);

      const patientFiles = await this.loadPatientCpapFiles(editForm.hn);

      await updateDoc(doc(db, COLLECTION_NAME, editForm.uuid), {
        "extendedProps.usageReports": patientFiles.usageReports,
        "extendedProps.questionnaires": patientFiles.questionnaires,
        updatedBy: this.getCurrentUserName(),
        updatedAt: serverTimestamp(),
      });

      this.setState((prevState) => {
        const updatedEditForm = {
          ...prevState.editForm,
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
        };

        const updatedSelectedFollowup = prevState.selectedFollowup
          ? {
            ...prevState.selectedFollowup,
            usageReports: patientFiles.usageReports,
            questionnaires: patientFiles.questionnaires,
          }
          : updatedEditForm;

        return {
          isUploading: false,
          editForm: updatedEditForm,
          selectedFollowup: updatedSelectedFollowup,
        };
      });

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      notifyTopRight("Upload success");

      await this.fetchData();
    } catch (error) {
      console.error(error);
      this.setState({ isUploading: false });
      notifyError("Upload error");
    }
  };

  formatWeightChangeForDisplay = (value) => {
    const text = String(safeText(value)).trim();

    if (!text) return "-";
    if (!/^[+-]?\d+(?:\.\d+)?(?:\s*-\s*[+-]?\d+(?:\.\d+)?)?$/.test(text)) {
      return text;
    }

    return `${text} kg`;
  };

  renderDetailRow = (label, value) => (
    <div
      className="mb-2"
      style={{
        display: "grid",
        gridTemplateColumns: "170px 1fr",
        gap: 8,
        fontSize: 13,
      }}
    >
      <div className="text-muted">{label}</div>
      <div style={{ fontWeight: 500, whiteSpace: "pre-wrap" }}>
        {value || value === 0 ? value : "-"}
      </div>
    </div>
  );

  renderDetailSection = (title, children) => {
    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 10,
          background: "#fff",
          padding: 14,
        }}
      >
        <h6
          className="mb-3"
          style={{
            fontWeight: 700,
            color: "#2f365f",
          }}
        >
          {title}
        </h6>

        {children}
      </div>
    );
  };

  formatNoteDate = (value) => {
    if (!value) return "-";

    if (value.toDate || value.seconds || value instanceof Date) {
      return this.formatFileUploadDate(value);
    }

    const text = String(value || "").trim();
    return text ? text.slice(0, 10) : "-";
  };

  renderNoteCard = ({ title, date, note, mutedText }) => (
    <div
      className="mb-2"
      style={{
        border: "1px solid #e9ecef",
        borderRadius: 8,
        background: "#f8f9fa",
        padding: 10,
        minHeight: 84,
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div style={{ fontWeight: 700, color: "#2f365f" }}>{title}</div>
        <div className="text-muted small">{this.formatNoteDate(date)}</div>
      </div>
      <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
        {note || <span className="text-muted">{mutedText || "-"}</span>}
      </div>
    </div>
  );

  renderFollowupNotesSection = (item = {}, previous = null) =>
    this.renderDetailSection(
      "Notes",
      <Row>
        <Col lg={6}>
          {this.renderNoteCard({
            title: "Current Note",
            date: item.date || item.followupDate,
            note: item.note,
            mutedText: "No current note",
          })}
        </Col>
        <Col lg={6}>
          {this.renderNoteCard({
            title: "Previous Note",
            date: previous && (previous.date || previous.recordedAt),
            note: previous && previous.note,
            mutedText: "No previous note",
          })}
        </Col>
      </Row>,
    );

  renderReadOnlyFileListSection = ({ title, files, emptyText }) => {
    const fileList = Array.isArray(files) ? files : [];

    return this.renderDetailSection(
      title,
      <Fragment>
        {fileList.length > 0 ? (
          fileList.map((file, index) => (
            <div
              key={`${file.storagePath || file.fileName}-${index}`}
              className="mb-2"
              style={{
                fontSize: 13,
                borderBottom:
                  index === fileList.length - 1 ? "none" : "1px solid #f1f1f1",
                paddingBottom: 6,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <i
                className="flaticon-381-file-2"
                style={{ color: "#dd0102", flex: "0 0 auto", marginTop: 2 }}
              />

              <button
                type="button"
                onClick={() => this.openFile(file)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                  textAlign: "left",
                  minWidth: 0,
                  flex: 1,
                }}
                title={file.fileName || file.storagePath || "-"}
              >
                <span
                  style={{
                    display: "block",
                    color: "#007bff",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    lineHeight: 1.25,
                  }}
                >
                  {file.fileName || file.storagePath || "-"}
                </span>

                <small
                  className="text-muted"
                  style={{
                    display: "block",
                    fontSize: 10,
                    lineHeight: 1.25,
                    marginTop: 2,
                  }}
                >
                  Uploaded: {this.formatFileUploadDate(file.uploadedAt)}
                  {file.uploadedBy ? ` by ${file.uploadedBy}` : ""}
                </small>
              </button>
            </div>
          ))
        ) : (
          <div className="text-muted small">{emptyText || "No File"}</div>
        )}
      </Fragment>,
    );
  };

  renderStatusBadge = (status) => {
    const statusConfig = getStatusConfig(status);

    return (
      <Badge
        variant={statusConfig.badge}
        style={{
          backgroundColor: statusConfig.color,
          color: "#3b2b46",
          borderRadius: 12,
          padding: "6px 9px",
          fontSize: 11,
          whiteSpace: "nowrap",
        }}
      >
        {statusConfig.label}
      </Badge>
    );
  };

  renderReadOnlyDetailBody = () => {
    const item = this.state.form || {};
    const previous = item.latestTherapyStatusRecord;
    const patientName = `${safeText(item.firstname)} ${safeText(
      item.lastname,
    )}`.trim();

    return (
      <Fragment>
        <div
          className="mb-3 p-3"
          style={{
            border: "1px solid #e9ecef",
            borderRadius: 12,
            background: "#f8f9fa",
          }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div style={{ minWidth: 0, flex: 1, paddingRight: 16 }}>
              <h5
                className="mb-1"
                style={{
                  fontWeight: 700,
                  color: "#2f365f",
                }}
              >
                {item.order || "-"}
              </h5>

              <div className="text-muted small">
                HN: {item.hn || "-"} · {patientName || "-"}
              </div>

              <div className="text-muted small">
                Ref HN: {item.hospitalRef || "-"}
              </div>

              <div className="text-muted small">
                Node: {item.node || "-"} · Customer: {item.customer || "-"}
              </div>
            </div>

            <div className="text-right">
              {this.renderStatusBadge(item.status)}
              <div className="small text-muted mt-1">{item.type || "-"}</div>
              <div className="small text-muted">
                Doctor: {this.getDoctorActionLabel(item.doctorActionStatus)}
              </div>
            </div>
          </div>
        </div>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Patient Information",
              <Fragment>
                {this.renderDetailRow("Date", item.date)}
                {this.renderDetailRow("Order", item.order)}
                {this.renderDetailRow("HN", item.hn)}
                {this.renderDetailRow("Name", patientName)}
                {this.renderDetailRow("Phone", item.phone)}
                {this.renderDetailRow("Ref HN", item.hospitalRef)}
                {this.renderDetailRow("Node", item.node)}
                {this.renderDetailRow("Customer", item.customer)}
                {this.renderDetailRow(
                  "AHI ก่อนใช้เครื่อง",
                  item.prePurchaseAhi,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Follow-Up Status",
              <Fragment>
                {this.renderDetailRow("Follow-Up Type", item.type)}
                {this.renderDetailRow("Status", item.status)}
                {this.renderDetailRow(
                  "Doctor Next Step",
                  this.getDoctorActionLabel(item.doctorActionStatus),
                )}
                {this.renderDetailRow(
                  "Technician Consult",
                  item.technicianConsult,
                )}
                {this.renderDetailRow("Doctor Consult", item.doctorConsult)}
                {this.renderDetailRow("File Count", this.getFileCount(item))}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "CPAP Device",
              <Fragment>
                {this.renderDetailRow("Serial Number", item.serialNumber)}
                {this.renderDetailRow("Brand", item.brand)}
                {this.renderDetailRow("Model", item.model)}
                {this.renderDetailRow("CPAP Type", item.cpapType)}
                {this.renderDetailRow("EPR", item.epr)}
                {this.renderDetailRow("Mask Type", item.maskType)}
                {this.renderDetailRow("Mask Size", item.maskSize)}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Pressure Setting",
              <Fragment>
                {this.renderDetailRow("Min Pressure", item.minPressure)}
                {this.renderDetailRow("Max Pressure", item.maxPressure)}
                {this.renderDetailRow(
                  "Current Ramp Time",
                  item.currentRampTime,
                )}
                {this.renderDetailRow(
                  "Humidifier System",
                  item.humidifierSystem,
                )}
                {this.renderDetailRow("Average Leak", item.averageLeak)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Objective Data & Compliance",
              <Fragment>
                {this.renderDetailRow("Data Period", item.dataPeriod)}
                {this.renderDetailRow("Date Summary", item.dateSummary)}
                {this.renderDetailRow(
                  "% การใช้งานที่มากกว่า 4 ชม.",
                  item.usageMoreThan4HoursPercent
                    ? `${item.usageMoreThan4HoursPercent}%`
                    : "-",
                )}
                {this.renderDetailRow(
                  "ชั่วโมงการใช้งานเฉลี่ย",
                  item.averageUsageHours,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Efficiency & Leak",
              <Fragment>
                {this.renderDetailRow("AHI เฉลี่ยต่อ ชม", item.averageAhi)}
                {this.renderDetailRow("แรงดัน 95%", item.pressure95Percent)}
                {this.renderDetailRow("Leak Rate", item.leakRate)}
                {this.renderDetailRow("การประเมิน Leak", item.leakAssessment)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Questionnaire & Symptoms",
              <Fragment>
                {this.renderDetailRow("ESS Score", item.essScore)}
                {this.renderDetailRow("โรคประจำตัว", item.underlyingDisease)}
                {this.renderDetailRow("น้ำหนักปัจจุบัน", item.currentWeight)}
                {this.renderDetailRow(
                  "การเปลี่ยนแปลงน้ำหนัก",
                  this.formatWeightChangeForDisplay(item.weightChange),
                )}
                {this.renderDetailRow(
                  "ความรู้สึกหลังตื่นนอน",
                  item.wakeFeeling,
                )}
                {this.renderDetailRow(
                  "อาการง่วงนอนตอนกลางวัน",
                  item.daytimeSleepiness,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Side Effects",
              <Fragment>
                {item.sideEffects && item.sideEffects.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {item.sideEffects.map((effect) => (
                      <Badge
                        key={effect}
                        variant="light"
                        style={{
                          border: "1px solid #ced4da",
                          color: "#495057",
                          padding: "6px 9px",
                          borderRadius: 12,
                          fontSize: 12,
                        }}
                      >
                        {effect}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">-</div>
                )}
                {this.renderDetailRow("อื่นๆ", item.sideEffectsOther)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderReadOnlyFileListSection({
              title: "CPAP Usage Report Files",
              files: item.usageReports,
              emptyText: "No Report",
            })}
          </Col>

          <Col lg={6}>
            {this.renderReadOnlyFileListSection({
              title: "Questionnaire Files",
              files: item.questionnaires,
              emptyText: "No Questionnaire",
            })}
          </Col>

          <Col lg={12} className="mt-2">
            {this.renderReadOnlyFileListSection({
              title: "Sleep Study Reports",
              files: item.sleepStudyReports,
              emptyText: "No Sleep Study Report",
            })}
          </Col>
        </Row>

        <div className="mt-3">
          <AnnualMaskPurchaseEventSection followup={item} readOnly />
        </div>

        {this.renderFollowupNotesSection(item, previous)}

        <Row>
          <Col lg={6}>
            {previous
              ? this.renderDetailSection(
                "Previous Doctor Decision",
                <Fragment>
                  {this.renderDetailRow("Date", previous.date)}
                  {this.renderDetailRow("Type", previous.type)}
                  {this.renderDetailRow("Status", previous.status)}
                  {this.renderDetailRow("Min Pressure", previous.minPressure)}
                  {this.renderDetailRow("Max Pressure", previous.maxPressure)}
                  {this.renderDetailRow(
                    "Current Ramp Time",
                    previous.currentRampTime,
                  )}
                  {this.renderDetailRow(
                    "Doctor Next Step",
                    this.getDoctorActionLabel(previous.doctorActionStatus),
                  )}
                  {this.renderDetailRow("Note", previous.note)}
                </Fragment>,
              )
              : this.renderDetailSection(
                "Previous Doctor Decision",
                <div className="text-muted small">
                  No previous doctor decision
                </div>,
              )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Audit",
              <div className="small text-muted">
                Created: {item._createdBy || "-"} ·{" "}
                {this.formatAuditDateTime(item._createdAt)}
                <br />
                Updated: {item._updatedBy || "-"} ·{" "}
                {this.formatAuditDateTime(item._updatedAt)}
              </div>,
            )}
          </Col>
        </Row>
      </Fragment>
    );
  };

  renderEventModal = () => {
    const isOpen = this.state.isAddEventModal || this.state.isEditEventModal;
    const isExisting = Boolean(this.state.form.uuid);
    const isReadOnlyDetail = isExisting && !this.state.isEditMode;
    const isStaffReadOnly = this.isStaffReadOnly();

    return (
      <Modal className="fade" show={isOpen} onHide={this.closeModal} size="xl">
        <Modal.Header closeButton>
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              paddingRight: 8,
            }}
          >
            <Modal.Title style={{ marginBottom: 0 }}>
              {isExisting
                ? this.state.isEditMode
                  ? "Edit CPAP Follow-Up"
                  : "CPAP Follow-Up Detail"
                : "Create CPAP Follow-Up"}
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "78vh", overflowY: "auto" }}>
          {isReadOnlyDetail ? (
            this.renderReadOnlyDetailBody()
          ) : (
            <Fragment>
              {this.renderSummaryHeader()}

              {this.state.form.latestTherapyStatusRecord &&
                this.renderDetailSection(
                  "Previous Doctor Decision",
                  <Fragment>
                    {this.renderDetailRow(
                      "Date",
                      this.state.form.latestTherapyStatusRecord.date,
                    )}
                    {this.renderDetailRow(
                      "Type",
                      this.state.form.latestTherapyStatusRecord.type,
                    )}
                    {this.renderDetailRow(
                      "Status",
                      this.state.form.latestTherapyStatusRecord.status,
                    )}
                    {this.renderDetailRow(
                      "Min Pressure",
                      this.state.form.latestTherapyStatusRecord.minPressure,
                    )}
                    {this.renderDetailRow(
                      "Max Pressure",
                      this.state.form.latestTherapyStatusRecord.maxPressure,
                    )}
                    {this.renderDetailRow(
                      "Current Ramp Time",
                      this.state.form.latestTherapyStatusRecord.currentRampTime,
                    )}
                    {this.renderDetailRow(
                      "Doctor Next Step",
                      this.getDoctorActionLabel(
                        this.state.form.latestTherapyStatusRecord
                          .doctorActionStatus,
                      ),
                    )}
                    {this.renderDetailRow(
                      "Note",
                      this.state.form.latestTherapyStatusRecord.note,
                    )}
                  </Fragment>,
                )}

              {this.renderTabs()}
              {this.renderActiveTab()}
            </Fragment>
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <div>
            {isExisting && this.getCurrentRole() === "super_admin" && (
              <Button variant="danger" onClick={this.onRemoveEvent}>
                Remove
              </Button>
            )}
          </div>

          <div>
            {isExisting && !this.state.isEditMode && !isStaffReadOnly && (
              <Button
                variant="secondary"
                className="mr-2"
                onClick={() => this.setState({ isEditMode: true })}
              >
                Edit
              </Button>
            )}

            {isExisting && this.state.isEditMode && (
              <Button
                variant="light"
                className="mr-2"
                onClick={() =>
                  this.setState({
                    isEditMode: false,
                    activeTab: "patient",
                  })
                }
                disabled={this.state.isUploading}
              >
                Cancel Edit
              </Button>
            )}

            <Button
              variant="light"
              className="mr-2"
              onClick={this.closeModal}
              disabled={this.state.isUploading}
            >
              Close
            </Button>

            {this.state.isEditMode && (
              <Button
                variant="primary"
                onClick={this.onSubmitEvent}
                disabled={this.state.isUploading}
              >
                {isExisting ? "Save" : "Submit"}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  renderWorkflowCard = () => {
    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 10,
          background: "#fbfcfd",
        }}
      >
        <Card.Body style={{ padding: "12px 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 360px", minWidth: 260 }}>
              <div
                style={{
                  fontWeight: 700,
                  color: "#2f365f",
                  marginBottom: 4,
                }}
              >
                CPAP Follow-Up Workflow
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: "#6c757d",
                  lineHeight: 1.55,
                }}
              >
                This page uses the same calendar event and moves it to the next
                follow-up date after doctor review. <br />
                The previous doctor decision is saved in the previous therapy
                section before the event is reset for the next follow-up.
              </div>
            </div>

            <div
              style={{
                flex: "1 1 320px",
                minWidth: 260,
                fontSize: 12,
                color: "#495057",
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                If doctor selects “ใช้เครื่องต่อ”
              </div>
              <div>
                → Save decision as previous doctor decision
                <br />
                → Move date to next routine follow-up
                <br />
                → Status becomes <b>CPAP Follow Up</b>
                <br />
                → Doctor Next Step resets to <b>รอการประเมินจากแพทย์</b>
              </div>
            </div>

            <div
              style={{
                flex: "1 1 320px",
                minWidth: 260,
                fontSize: 12,
                color: "#495057",
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                If doctor selects “ปรับ Pressure”
              </div>
              <div>
                → Save decision as previous doctor decision
                <br />
                → Keep the same follow-up type
                <br />
                → Move date +14 days
                <br />
                → Status becomes <b>Technical Solution</b>
                <br />
                → Doctor Next Step stays <b>ปรับ Pressure</b>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: "1px solid #e9ecef",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: "#2f365f",
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              Status Color Guide
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 8,
              }}
            >
              {STATUS_OPTIONS.map((item) => (
                <div
                  key={item.value}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: "7px 9px",
                    border: "1px solid #eef0f2",
                    borderRadius: 8,
                    background: "#ffffff",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      backgroundColor: item.color,
                      border: "1px solid rgba(0,0,0,0.12)",
                      flex: "0 0 auto",
                      marginTop: 2,
                    }}
                  />

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#343a40",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.label}
                    </div>

                    <div
                      style={{
                        fontSize: 11,
                        color: "#6c757d",
                        lineHeight: 1.35,
                        marginTop: 1,
                      }}
                    >
                      {item.helper}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card.Body>
      </div>
    );
  };

  renderFilters = () => {
    const { filters } = this.state;
    const isSleepPhysician = this.getCurrentRole() === "sleep_physician";
    const isSuperAdmin = this.getCurrentRole() === "super_admin";
    const isStaffReadOnly = this.isStaffReadOnly();
    const currentUserName = this.getCurrentUserName();

    const nodeOptions = [
      "All Node",
      ...this.state.nodeList.map((item) => item.nodename),
    ];
    const customerOptions = [
      "All Customer",
      ...Array.from(
        new Set(
          this.state.filterCustomerList
            .map((item) => item.displayName || item.id)
            .filter(Boolean),
        ),
      ),
    ];

    const doctorOptions = isSleepPhysician
      ? [currentUserName].filter(Boolean)
      : this.state.doctorList.filter((item) => item !== "กรุณาเลือก");

    return (
      <div
        className="mb-3 p-3"
        style={{
          background: "#fff",
          border: "1px solid #e9ecef",
          borderRadius: 8,
          height: "auto",
          minHeight: "auto",
          maxHeight: "none",
          overflow: "visible",
        }}
      >
        <Row>
          {!isSleepPhysician && (
            <Col lg={2} md={4} sm={6} className="mb-2">
              <label className="small text-muted mb-1">Node</label>
              <select
                className="form-control form-control-sm"
                value={filters.node}
                onChange={(e) =>
                  this.handleFilterChange("node", e.target.value)
                }
              >
                {nodeOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Col>
          )}

          {isSuperAdmin && (
            <Col lg={2} md={4} sm={6} className="mb-2">
              <label className="small text-muted mb-1">Customer</label>
              <select
                className="form-control form-control-sm"
                value={filters.customer || "All Customer"}
                onChange={(e) =>
                  this.handleFilterChange("customer", e.target.value)
                }
              >
                {customerOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Col>
          )}

          <Col lg={2} md={4} sm={6} className="mb-2">
            <label className="small text-muted mb-1">Status</label>
            <select
              className="form-control form-control-sm"
              value={filters.status}
              onChange={(e) =>
                this.handleFilterChange("status", e.target.value)
              }
            >
              <option value="All Status">All Status</option>
              {STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-2">
            <label className="small text-muted mb-1">Type</label>
            <select
              className="form-control form-control-sm"
              value={filters.type}
              onChange={(e) => this.handleFilterChange("type", e.target.value)}
            >
              <option value="All Type">All Type</option>
              {FOLLOWUP_TYPE_LIST.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </Col>

          <Col lg={2} md={4} sm={6} className="mb-2">
            <label className="small text-muted mb-1">Average AHI</label>
            <select
              className="form-control form-control-sm"
              value={filters.averageAhi}
              onChange={(e) =>
                this.handleFilterChange("averageAhi", e.target.value)
              }
            >
              <option value="All Average AHI">All Average AHI</option>
              <option value="EMPTY_OR_ZERO">ว่าง / 0</option>
              <option value="LESS_THAN_30">น้อยกว่า 30</option>
              <option value="BETWEEN_30_60">30 - 60</option>
              <option value="MORE_THAN_60">มากกว่า 60</option>
            </select>
          </Col>

          {!isStaffReadOnly && (
            <Col lg={2} md={4} sm={6} className="mb-2">
              <label className="small text-muted mb-1">Doctor</label>
              <select
                className="form-control form-control-sm"
                value={filters.doctor}
                onChange={(e) =>
                  this.handleFilterChange("doctor", e.target.value)
                }
              >
                {!isSleepPhysician && (
                  <option value="All Doctor">All Doctor</option>
                )}
                {doctorOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Col>
          )}

          <Col lg={2} md={4} sm={6} className="mb-2">
            <label className="small text-muted mb-1">Doctor Action</label>
            <select
              className="form-control form-control-sm"
              value={filters.doctorActionStatus}
              onChange={(e) =>
                this.handleFilterChange("doctorActionStatus", e.target.value)
              }
            >
              <option value="All Doctor Action">All Doctor Action</option>
              {DOCTOR_ACTION_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </Col>
        </Row>
      </div>
    );
  };

  isOnlineReportCpapModel = (modelValue) => {
    const model = safeText(modelValue).trim().toLowerCase();

    const onlineModels = [
      "airsense 10 autoset",
      "airsense 10 autoset for her",
      "airsense 10 elite",
      "airsense 11 autoset",
      "airmini autoset",
    ];

    return onlineModels.includes(model);
  };

  renderCalendarEvent = (info) => {
    const ep = info.event.extendedProps || {};
    const typeLabel = String(safeText(ep.type))
      .replace(/^CPAP Follow-Up /, "")
      .replace(/^(\d+)([MY])$/, "$1 $2");

    const name = `${safeText(ep.firstname)} ${safeText(ep.lastname)}`.trim();
    const doctorAction = this.getDoctorActionLabel(ep.doctorActionStatus);
    const ahiHighlight = this.getAverageAhiInlineStyle(ep.averageAhi);
    const averageAhiNumber = parseAverageAhi(ep.averageAhi);
    const hasAverageAhi = String(safeText(ep.averageAhi)).trim() !== "";
    const canGetOnlineReport = this.isOnlineReportCpapModel(ep.model);

    const title = [
      ep.order ? `Order: ${ep.order}` : "",
      canGetOnlineReport ? `Online Report: Available (${ep.model})` : "",
      name ? `Patient: ${name}` : "",
      ep.type ? `Type: ${ep.type}` : "",
      doctorAction ? `Doctor Next Step: ${doctorAction}` : "",
      ep.status ? `Status: ${ep.status}` : "",
      ep.hn ? `HN: ${ep.hn}` : "",
      ep.hospitalRef ? `Ref HN: ${ep.hospitalRef}` : "",
      ep.node ? `Node: ${ep.node}` : "",
      ep.customer ? `Customer: ${ep.customer}` : "",
      ep.phone ? `Phone: ${ep.phone}` : "",
      ep.model ? `CPAP Model: ${ep.model}` : "",
      ep.prePurchaseAhi ? `AHI ก่อนใช้เครื่อง: ${ep.prePurchaseAhi}` : "",
      hasAverageAhi ? `Average AHI: ${ep.averageAhi}` : "",
      ep.usageMoreThan4HoursPercent
        ? `>4h: ${ep.usageMoreThan4HoursPercent}%`
        : "",
      ep.leakAssessment ? `Leak: ${ep.leakAssessment}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    return (
      <div
        title={title}
        style={{
          lineHeight: 1.22,
          textAlign: "center",
          overflow: "hidden",
          color: "#2f365f",
          width: "100%",
          padding: ahiHighlight.label ? "4px 5px" : "2px 3px",
          borderRadius: 8,
          transition: "all 0.2s ease",
          ...ahiHighlight.wrapperStyle,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "36px minmax(0, 1fr) 36px",
            alignItems: "center",
            gap: 4,
            minWidth: 0,
            width: "100%",
          }}
        >
          {canGetOnlineReport && (
            <span
              title={`Online report available: ${ep.model}`}
              role="img"
              aria-label={`Online report available: ${ep.model}`}
              style={{
                gridColumn: 1,
                gridRow: 1,
                justifySelf: "start",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              🌐
            </span>
          )}

          <div
            style={{
              fontWeight: ahiHighlight.label ? 900 : 800,
              fontSize: 11,
              color: "#2f365f",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              gridColumn: 2,
              gridRow: 1,
            }}
          >
            {ep.order || "No Order"}
          </div>
          {typeLabel && (
            <span
              style={{
                gridColumn: 3,
                gridRow: 1,
                justifySelf: "end",
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              ({typeLabel})
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "#2f365f",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || "No patient name"}
        </div>

        <div
          style={{
            fontSize: 10,
            color: "#4f577a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {doctorAction || "-"}
        </div>

        <div
          style={{
            fontSize: 10,
            fontWeight: ahiHighlight.label ? 800 : 600,
            color:
              averageAhiNumber > 60
                ? "#991b1b"
                : averageAhiNumber >= 30
                  ? "#92400e"
                  : "#4f577a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          AHI: {hasAverageAhi ? ep.averageAhi : "-"}
        </div>
      </div>
    );
  };

  getAverageAhiInlineStyle = (averageAhiValue) => {
    const averageAhi = parseAverageAhi(averageAhiValue);

    if (averageAhi === null) {
      return {
        wrapperStyle: {},
        badgeStyle: {},
        label: "",
      };
    }

    if (averageAhi > 60) {
      return {
        wrapperStyle: {
          border: "2px solid #dc2626",
          boxShadow:
            "0 0 0 3px rgba(220, 38, 38, 0.28), 0 0 14px rgba(220, 38, 38, 0.75)",
          background:
            "linear-gradient(90deg, rgba(220,38,38,0.18), rgba(255,255,255,0.04))",
          borderRadius: 8,
        },
        badgeStyle: {
          backgroundColor: "#dc2626",
          color: "#ffffff",
        },
        label: "AHI > 60",
      };
    }

    if (averageAhi >= 30 && averageAhi <= 60) {
      return {
        wrapperStyle: {
          border: "2px solid #f59e0b",
          boxShadow:
            "0 0 0 2px rgba(245, 158, 11, 0.24), 0 0 10px rgba(245, 158, 11, 0.55)",
          background:
            "linear-gradient(90deg, rgba(245,158,11,0.18), rgba(255,255,255,0.04))",
          borderRadius: 8,
        },
        badgeStyle: {
          backgroundColor: "#f59e0b",
          color: "#111827",
        },
        label: "AHI 30-60",
      };
    }

    return {
      wrapperStyle: {},
      badgeStyle: {},
      label: "",
    };
  };

  render() {
    const isStaffReadOnly = this.isStaffReadOnly();

    return (
      <Fragment>
        <Toast />
        {this.renderFilePreviewModal()}
        {this.renderEventModal()}

        <div className="animated fadeIn">
          <Row>
            <Col xl={12}>

              {!isStaffReadOnly && (
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={this.importFollowupXlsxRef}
                  style={{ display: "none" }}
                  onChange={this.importFollowupEventsFromXlsx}
                />
              )}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="mb-0">CPAP Follow-Up Calendar</h3>
                  <small className="text-muted">
                    Calendar for CPAP follow-up, usage support, doctor consult,
                    and adherence review.
                  </small>
                </div>
                <div>
                  <Button
                    as={Link}
                    to="/cpap-followup-guide"
                    className="btn btn-outline-info mr-2"
                  >
                    Guide TH/EN
                  </Button>

                  {!isStaffReadOnly && (
                    <Fragment>
                      <Button
                        className="btn btn-success mr-2"
                        onClick={this.handleImportFollowupXlsxClick}
                        disabled={this.state.isLoading}
                      >
                        + Import XLSX
                      </Button>

                      <Button
                        className="btn btn-primary"
                        onClick={() => this.openAddModal()}
                      >
                        + New Follow-Up
                      </Button>
                    </Fragment>
                  )}
                </div>
              </div>

              {this.renderWorkflowCard()}

              {this.renderFilters()}

              <Card style={{ height: "auto", minHeight: "auto" }}>
                <div className="booking-calendar-search px-2 pb-2">
                  <div className="booking-calendar-search-inner">
                    <i className="flaticon-381-search-2 booking-calendar-search-icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search order, HN, or patient name..."
                      value={this.state.followupSearchText}
                      onChange={this.onSearchFollowup}
                    />
                    {this.state.followupSearchText && (
                      <Button
                        type="button"
                        variant="light"
                        className="booking-calendar-search-clear"
                        onClick={this.onClearFollowupSearch}
                      >
                        Clear
                      </Button>
                    )}
                    <span className="booking-calendar-search-count">
                      {this.state.filteredEvents.length} matching across all dates
                    </span>
                  </div>
                </div>
                <Card.Body style={{ height: "auto", minHeight: "auto" }}>
                  {!this.state.isLoading ? (
                    <FullCalendar
                      ref={this.calendarComponentRef}
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                        listPlugin,
                        multiMonthPlugin,
                      ]}
                      initialView="dayGridMonth"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right:
                          "dayGridMonth,timeGridWeek,listMonth,multiMonthYear",
                      }}
                      height="auto"
                      selectable={!isStaffReadOnly}
                      editable={false}
                      events={this.state.filteredEvents}
                      dateClick={
                        isStaffReadOnly
                          ? undefined
                          : (arg) => this.openAddModal(arg.dateStr)
                      }
                      eventClick={this.openEditModal}
                      eventContent={this.renderCalendarEvent}
                    />
                  ) : (
                    <div className="text-center p-5">Loading...</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Fragment>
    );
  }
}

export default CPAPFollowupCalendar;
